import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { XMarkIcon, PlusIcon, TrashIcon, ArrowPathIcon, MagnifyingGlassIcon, FolderIcon, CloudArrowDownIcon } from '@heroicons/react/24/outline'
import IconSearchModal from './IconSearchModal'
import LocalIconsModal from './LocalIconsModal'
import toast from 'react-hot-toast'
import api, { extractPageIcons, downloadIconToLocal, updateConfig, getConfigByType } from '../api/config'
import ProtocolSelector from './ProtocolSelector'

const ServiceEditModal = ({
  isOpen,
  onClose,
  onSave,
  service = null,
  groupName = '',
  availableGroups = []
}) => {
  const [selectedGroup, setSelectedGroup] = useState(groupName)
  const [widgetMappings, setWidgetMappings] = useState([])
  const [isExtractingIcon, setIsExtractingIcon] = useState(false)
  const [availableIcons, setAvailableIcons] = useState([])
  const [showIconSelector, setShowIconSelector] = useState(false)
  const [showIconSearch, setShowIconSearch] = useState(false)
  const [showLocalIcons, setShowLocalIcons] = useState(false)
  const [customFields, setCustomFields] = useState([])
  const [showCustomFieldsHelp, setShowCustomFieldsHelp] = useState(false)
  const [videoInfoLoading, setVideoInfoLoading] = useState(false)
  const [videoInfo, setVideoInfo] = useState(null)
  const [statusStyle, setStatusStyle] = useState('') // 全局状态样式设置


  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useForm()

  const watchedWidgetType = watch('widget.type')
  const watchedHref = watch('href')
  const watchedVideoId = watch('widget.videoId')
  const getManagerBaseUrl = () => window.location.origin

  // Bilibili 扫码登录弹窗状态
  const [showBiliLogin, setShowBiliLogin] = useState(false)
  const [biliLoginKey, setBiliLoginKey] = useState('')
  const [biliQrUrl, setBiliQrUrl] = useState('')
  const [biliLoginStatus, setBiliLoginStatus] = useState('idle') // idle | pending | success | failed
  // 初始化时获取登录状态，决定按钮默认样式与可点击性
  useEffect(() => {
    const checkState = async () => {
      try {
        const r = await fetch('/api/bilibili/login/state')
        const s = await r.json()
        if (s.success && s.loggedIn) {
          setBiliLoginStatus('success')
        }
      } catch (_) { }
    }
    checkState()
  }, [])
  const [biliCookieInput, setBiliCookieInput] = useState('')
  const [biliPollTimer, setBiliPollTimer] = useState(null)

  // 检查图标是否可用
  const checkIconAvailability = async (iconUrl) => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
      img.src = iconUrl
      // 设置超时
      setTimeout(() => resolve(false), 3000)
    })
  }

  // 智能URL处理函数
  const normalizeUrl = (url) => {
    if (!url || typeof url !== 'string') return null

    try {
      const trimmedUrl = url.trim()
      if (!trimmedUrl) return null

      // 如果已经有协议，直接返回
      if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
        return trimmedUrl
      }

      // 如果以 // 开头，添加 http:
      if (trimmedUrl.startsWith('//')) {
        return 'http:' + trimmedUrl
      }

      // 其他情况，添加 http://
      return 'http://' + trimmedUrl
    } catch (error) {
      console.error('Error in normalizeUrl:', error)
      return null
    }
  }

  // 自动提取图标的函数（前端组合：后端 rel=icon + 本地推断 + 第三方服务）
  const extractIconsFromUrl = async (url) => {
    if (!url) return []

    try {
      setIsExtractingIcon(true)

      // 智能处理URL格式
      const normalizedUrl = normalizeUrl(url)
      if (!normalizedUrl) {
        throw new Error('无效的URL格式')
      }

      // 解析URL
      const urlObj = new URL(normalizedUrl)
      const baseUrl = `${urlObj.protocol}//${urlObj.host}`
      const serviceName = urlObj.hostname.replace('www.', '').split('.')[0] // 提取服务名称，移除www前缀

      // 调用后端接口提取 <link rel=icon> 等
      let extracted = []
      try {
        const resp = await extractPageIcons(normalizedUrl)
        if (resp?.success && Array.isArray(resp.icons)) {
          extracted = resp.icons.map(i => ({ url: i.url, name: `HTML ${i.rel || 'icon'}${i.sizes ? ` (${i.sizes})` : ''}`, type: 'favicon' }))
        }
      } catch (_) { }

      // 构建可能的图标列表（本地推断 + 第三方）
      const possibleIcons = [
        // 常见的favicon路径
        { url: `${baseUrl}/favicon.ico`, name: 'Favicon (ICO)', type: 'favicon' },
        { url: `${baseUrl}/favicon.png`, name: 'Favicon (PNG)', type: 'favicon' },
        { url: `${baseUrl}/favicon.svg`, name: 'Favicon (SVG)', type: 'favicon' },
        { url: `${baseUrl}/apple-touch-icon.png`, name: 'Apple Touch Icon', type: 'favicon' },
        { url: `${baseUrl}/apple-touch-icon-precomposed.png`, name: 'Apple Touch Icon (Precomposed)', type: 'favicon' },
        { url: `${baseUrl}/android-chrome-192x192.png`, name: 'Android Chrome Icon (192px)', type: 'favicon' },
        { url: `${baseUrl}/android-chrome-512x512.png`, name: 'Android Chrome Icon (512px)', type: 'favicon' },
        { url: `${baseUrl}/mstile-150x150.png`, name: 'MS Tile Icon', type: 'favicon' },

        // Google Favicon Service - 多种尺寸
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=16`, name: 'Google Favicon (16px)', type: 'favicon' },
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=32`, name: 'Google Favicon (32px)', type: 'favicon' },
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=64`, name: 'Google Favicon (64px)', type: 'favicon' },
        { url: `https://www.google.com/s2/favicons?domain=${urlObj.hostname}&sz=128`, name: 'Google Favicon (128px)', type: 'favicon' },

        // 其他Favicon服务
        { url: `https://icons.duckduckgo.com/ip3/${urlObj.hostname}.ico`, name: 'DuckDuckGo Favicon', type: 'favicon' },
        { url: `https://favicons.githubusercontent.com/${urlObj.hostname}`, name: 'GitHub Favicon Service', type: 'favicon' },

        // Dashboard Icons (基于服务名称猜测)
        { url: `${serviceName}.png`, name: `Dashboard Icon (${serviceName}.png)`, type: 'dashboard' },
        { url: `${serviceName}.svg`, name: `Dashboard Icon (${serviceName}.svg)`, type: 'dashboard' },

        // Simple Icons - 多种变体
        { url: `si-${serviceName}`, name: `Simple Icon (si-${serviceName})`, type: 'simple' },
        { url: `simple-icons:${serviceName}`, name: `Simple Icons (${serviceName})`, type: 'iconify' },

        // Material Design Icons
        { url: `mdi-${serviceName}`, name: `Material Icon (mdi-${serviceName})`, type: 'material' },
        { url: `mdi:${serviceName}`, name: `MDI (${serviceName})`, type: 'iconify' },

        // Brand Icons
        { url: `logos:${serviceName}`, name: `Logo (${serviceName})`, type: 'iconify' },
        { url: `devicon:${serviceName}`, name: `DevIcon (${serviceName})`, type: 'iconify' },
        { url: `skill-icons:${serviceName}`, name: `Skill Icon (${serviceName})`, type: 'iconify' }
      ]

      // 检查哪些图标可用
      const availableIcons = []

      // 先加入从HTML提取的候选，且去重
      const seen = new Set()
      for (const icon of extracted) {
        if (!seen.has(icon.url)) {
          const ok = await checkIconAvailability(icon.url)
          if (ok) {
            availableIcons.push(icon)
            seen.add(icon.url)
          }
        }
      }

      // 对于favicon类型，检查实际可用性
      for (const icon of possibleIcons.filter(i => i.type === 'favicon')) {
        const isAvailable = await checkIconAvailability(icon.url)
        if (isAvailable) {
          if (!seen.has(icon.url)) {
            availableIcons.push(icon)
            seen.add(icon.url)
          }
        }
      }

      // 对于其他类型，直接添加（因为无法预先检查）
      for (const icon of possibleIcons.filter(i => i.type !== 'favicon')) {
        if (!seen.has(icon.url)) {
          availableIcons.push(icon)
          seen.add(icon.url)
        }
      }

      return availableIcons

    } catch (error) {
      console.error('提取图标失败:', error)
      return []
    } finally {
      setIsExtractingIcon(false)
    }
  }

  // 手动提取图标
  const handleExtractIcon = async () => {
    const href = watchedHref
    if (!href) {
      alert('请先输入访问地址\n\n💡 支持格式：\n• 完整URL：https://example.com\n• 简化格式：example.com\n• 子域名：app.example.com')
      return
    }

    const icons = await extractIconsFromUrl(href)
    if (icons.length > 0) {
      setAvailableIcons(icons)
      setShowIconSelector(true)
    } else {
      alert('无法找到可用的图标，请手动输入图标路径或使用图标搜索功能')
    }
  }

  // 选择图标：下载到本地 icons 目录并写入完整服务器地址
  const handleSelectIcon = async (iconUrl) => {
    try {
      // 优先使用服务名作为基名
      const href = watchedHref || ''
      let preferred = ''
      try {
        const u = new URL(normalizeUrl(href) || '')
        preferred = u.hostname.replace(/^www\./, '').split('.')[0]
      } catch (_) { }

      const resp = await downloadIconToLocal(iconUrl, preferred || undefined)
      if (resp?.success && resp.iconPath) {
        // 生成完整的服务器地址
        const serverUrl = `${window.location.protocol}//${window.location.host}`
        const fullIconPath = `${serverUrl}/${resp.iconPath}`
        setValue('icon', fullIconPath)
        if (resp.skipped && resp.reason === 'duplicate') {
          toast.success('图标已存在，直接使用本地图标')
        } else {
          toast.success('图标已保存到本地并应用')
        }
      } else {
        // 回退：直接使用远程URL
        setValue('icon', iconUrl)
        toast('已应用远程图标', { icon: 'ℹ️' })
      }
    } catch (e) {
      console.error('保存图标失败:', e)
      setValue('icon', iconUrl)
      toast.error('保存到本地失败，已使用远程图标')
    } finally {
      setShowIconSelector(false)
      setAvailableIcons([])
    }
  }

  // 从图标搜索选择图标
  const handleSelectSearchIcon = (iconPath) => {
    setValue('icon', iconPath)
    setShowIconSearch(false)
  }

  // 从本地图标库选择图标
  const handleSelectLocalIcon = (iconPath) => {
    setValue('icon', iconPath)
    setShowLocalIcons(false)
  }

  // 获取图标预览URL
  const getIconPreviewUrl = (iconValue) => {
    if (!iconValue || typeof iconValue !== 'string') return null

    try {
      // 如果是完整URL
      if (iconValue.startsWith('http')) {
        return iconValue
      }

      // 如果是本地图标路径 /icons/xxx
      if (iconValue.startsWith('/icons/')) {
        return iconValue
      }

      // 如果是Dashboard Icons - 支持所有常见图片格式
      if (iconValue.endsWith('.png') || iconValue.endsWith('.svg') || iconValue.endsWith('.webp') ||
        iconValue.endsWith('.jpg') || iconValue.endsWith('.jpeg') || iconValue.endsWith('.ico') ||
        iconValue.endsWith('.gif') || iconValue.endsWith('.bmp') || iconValue.endsWith('.tiff')) {
        return `https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/png/${iconValue}`
      }

      // 如果是Simple Icons
      if (iconValue.startsWith('si-')) {
        const iconName = iconValue.replace('si-', '')
        return `https://cdn.jsdelivr.net/npm/simple-icons@v9/${iconName}.svg`
      }

      // 如果是Material Design Icons
      if (iconValue.startsWith('mdi-')) {
        const iconName = iconValue.replace('mdi-', '')
        return `https://cdn.jsdelivr.net/npm/@mdi/svg@7.2.96/svg/${iconName}.svg`
      }

      return null
    } catch (error) {
      console.error('Error in getIconPreviewUrl:', error)
      return null
    }
  }

  // 获取Bilibili视频信息
  const fetchVideoInfo = async (videoId) => {
    if (!videoId || (!videoId.startsWith('BV') && !videoId.startsWith('av'))) {
      alert('请输入有效的视频ID（BV号或AV号）')
      return
    }

    setVideoInfoLoading(true)
    try {
      // 获取API基础地址
      let apiBaseUrl = getValues('widget.apiBaseUrl')
      if (!apiBaseUrl) {
        // 自动检测API基础地址
        apiBaseUrl = getManagerBaseUrl()
      }

      // 确保API基础地址不以斜杠结尾
      apiBaseUrl = apiBaseUrl.replace(/\/$/, '')

      const response = await fetch(`${apiBaseUrl}/api/bilibili/video/${videoId}`)
      const data = await response.json()

      if (response.ok && data.title) {
        setVideoInfo(data)
        // 如果服务名称为空，自动填入视频标题
        const currentName = getValues('name')
        if (!currentName) {
          setValue('name', data.title)
        }
        // 保存提取到的标题用于后续使用
        setValue('widget.extractedTitle', data.title)
        alert(`✅ 成功获取视频信息：${data.title}`)
      } else {
        throw new Error(data.error || '获取视频信息失败')
      }
    } catch (error) {
      console.error('获取视频信息失败:', error)
      alert(`❌ 获取视频信息失败：${error.message}`)
    } finally {
      setVideoInfoLoading(false)
    }
  }

  // ====== Bilibili 扫码登录（占位后端对接） ======
  const startBiliLogin = async () => {
    try {
      setBiliLoginStatus('pending')
      setShowBiliLogin(true)
      // 创建二维码
      const resp = await fetch('/api/bilibili/login/create', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
      const data = await resp.json()
      if (!data.success) throw new Error(data.error || '创建二维码失败')
      setBiliLoginKey(data.loginKey)
      setBiliQrUrl(data.qrUrl)

      // 轮询状态（占位后端当前返回 pending）
      const timer = setInterval(async () => {
        try {
          const r = await fetch(`/api/bilibili/login/status/${data.loginKey}`)
          const s = await r.json()
          if (s.success) {
            // 更新状态展示
            setBiliLoginStatus(s.status || 'pending')
            // 成功或已失效时停止轮询
            if (s.status === 'success' || s.status === 'expired') {
              clearInterval(timer)
              setBiliPollTimer(null)
              // success: 不弹窗，按钮将切换为“已登录”
              if (s.status === 'success') {
                setShowBiliLogin(false)
              }
            }
          }
        } catch (e) { }
      }, 3000)
      setBiliPollTimer(timer)
    } catch (e) {
      setBiliLoginStatus('failed')
      alert(`创建登录失败：${e.message}`)
    }
  }

  const stopBiliLogin = () => {
    if (biliPollTimer) {
      clearInterval(biliPollTimer)
      setBiliPollTimer(null)
    }
    setShowBiliLogin(false)
    setBiliLoginKey('')
    setBiliQrUrl('')
    setBiliLoginStatus('idle')
  }

  const submitBiliCookie = async () => {
    try {
      if (!biliCookieInput.trim()) {
        return
      }
      const resp = await fetch('/api/bilibili/login/set-cookie', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cookie: biliCookieInput.trim() })
      })
      const data = await resp.json()
      if (!data.success) throw new Error(data.error || '保存失败')

      // 按“扫码成功”后的流程处理：验证成功即关闭弹窗并停止轮询
      if (data.verified) {
        if (biliPollTimer) {
          clearInterval(biliPollTimer)
          setBiliPollTimer(null)
        }
        setBiliLoginStatus('success')
        // 延迟关闭，避免事件冒泡触发父层覆盖层的关闭逻辑
        setTimeout(() => setShowBiliLogin(false), 0)
        // 可选：提示未读消息数
        if (typeof data.unread !== 'undefined') {
          try { console.log('Bilibili 未读消息数:', data.unread) } catch (_) { }
        }
      } else {
        setBiliLoginStatus('failed')
        alert('Cookie 无效或验证失败，请检查后重试')
      }
    } catch (e) {
      setBiliLoginStatus('failed')
      alert(`保存失败：${e.message}`)
    }
  }

  const logoutBilibili = async () => {
    try {
      await fetch('/api/bilibili/logout', { method: 'POST' })
      // 立即更新本地登录状态与界面
      setBiliLoginStatus('idle')
      setShowBiliLogin(false)
      // 复核一次后端登录状态，确保一致
      try {
        const r = await fetch('/api/bilibili/login/state')
        const s = await r.json()
        setBiliLoginStatus(s && s.success && s.loggedIn ? 'success' : 'idle')
      } catch (_) { }
    } catch (e) {
      // 静默失败
    }
  }

  // 保存全局状态样式设置
  const handleStatusStyleChange = async (newStyle) => {
    try {
      setStatusStyle(newStyle)
      const response = await getConfigByType('settings')
      const settings = response.data || {}
      settings.statusStyle = newStyle
      await updateConfig('settings', settings)
      toast.success('状态样式已更新')
    } catch (error) {
      console.error('保存状态样式失败:', error)
      toast.error('保存状态样式失败')
      // 回滚状态
      const response = await getConfigByType('settings')
      const settings = response.data || {}
      setStatusStyle(settings.statusStyle || '')
    }
  }



  // 加载全局状态样式设置
  useEffect(() => {
    const loadStatusStyle = async () => {
      try {
        const response = await getConfigByType('settings')
        const settings = response.data || {}
        setStatusStyle(settings.statusStyle || '')
      } catch (error) {
        console.error('加载状态样式设置失败:', error)
      }
    }

    if (isOpen) {
      loadStatusStyle()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && service) {
      const serviceName = Object.keys(service)[0]
      const serviceConfig = service[serviceName]

      // 设置基本信息
      setValue('name', serviceName)
      setValue('icon', serviceConfig.icon || '')
      setValue('href', serviceConfig.href || '')
      setValue('description', serviceConfig.description || '')
      setValue('target', serviceConfig.target || '_blank')

      // 设置 Ping 配置
      const hasPingConfig = serviceConfig.ping && serviceConfig.ping.trim() !== ''
      setValue('enablePing', hasPingConfig)
      setValue('ping', serviceConfig.ping || '')
      setValue('pingMethod', serviceConfig.pingMethod || 'http')
      setValue('pingTimeout', serviceConfig.pingTimeout || 5000)

      // 设置小部件信息
      if (serviceConfig.widget) {
        setValue('widget.type', serviceConfig.widget.type || '')
        setValue('widget.url', serviceConfig.widget.url || '')
        setValue('widget.key', serviceConfig.widget.key || '')
        setValue('widget.username', serviceConfig.widget.username || '')
        setValue('widget.password', serviceConfig.widget.password || '')
        setValue('widget.env', serviceConfig.widget.env || '')

        // 设置 Glances 特殊配置
        if (serviceConfig.widget.type === 'glances') {
          // 设置选中的指标（单选）
          if (serviceConfig.widget.selectedMetric) {
            setValue('widget.selectedMetric', serviceConfig.widget.selectedMetric)
          } else if (serviceConfig.widget.fields) {
            // 兼容旧版本的复选框数据
            if (Array.isArray(serviceConfig.widget.fields)) {
              // 如果是数组格式，取第一个作为选中项
              if (serviceConfig.widget.fields.length > 0) {
                setValue('widget.selectedMetric', serviceConfig.widget.fields[0])
              }
            } else if (typeof serviceConfig.widget.fields === 'object') {
              // 如果是对象格式，找到第一个为 true 的字段
              const selectedField = Object.keys(serviceConfig.widget.fields).find(field =>
                serviceConfig.widget.fields[field]
              )
              if (selectedField) {
                setValue('widget.selectedMetric', selectedField)
              }
            }
          }
          setValue('widget.disk', serviceConfig.widget.disk || '')
          setValue('widget.interface', serviceConfig.widget.interface || '')
          setValue('widget.warn', serviceConfig.widget.warn || '')
          setValue('widget.critical', serviceConfig.widget.critical || '')
          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 30)
          setValue('widget.version', serviceConfig.widget.version || 4)

          // 加载自定义字段
          if (serviceConfig.widget.customFields) {
            setCustomFields(serviceConfig.widget.customFields)
          } else {
            setCustomFields([])
          }
        }

        // 设置 Mikrotik 特殊配置
        if (serviceConfig.widget.type === 'mikrotik') {
          // 加载 fields 配置
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              serviceConfig.widget.fields.forEach(field => {
                setValue(`widget.fields.${field}`, true)
              })
            }
          }
        }

        // 设置 AdGuard Home 特殊配置
        if (serviceConfig.widget.type === 'adguard') {
          // 加载 fields 配置
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              serviceConfig.widget.fields.forEach(field => {
                if (field === 'queries') setValue('widget.showQueries', true)
                if (field === 'blocked') setValue('widget.showBlocked', true)
                if (field === 'filtered') setValue('widget.showFiltered', true)
                if (field === 'latency') setValue('widget.showLatency', true)
              })
            }
          }
        }

        // 设置 Jackett 特殊配置
        if (serviceConfig.widget.type === 'jackett') {
          // 加载字段配置 - 支持数组格式和对象格式
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              // 如果是数组格式，转换为对象格式
              setValue('widget.fields.configured', serviceConfig.widget.fields.includes('configured'))
              setValue('widget.fields.errored', serviceConfig.widget.fields.includes('errored'))
            } else if (typeof serviceConfig.widget.fields === 'object') {
              // 对象格式，直接设置
              setValue('widget.fields.configured', serviceConfig.widget.fields.configured || false)
              setValue('widget.fields.errored', serviceConfig.widget.fields.errored || false)
            }
          } else {
            // 默认配置 - 启用 configured 字段
            setValue('widget.fields.configured', true)
            setValue('widget.fields.errored', false)
          }

          // 加载旧的显示配置（兼容性）
          if (serviceConfig.widget.showConfigured !== undefined) {
            setValue('widget.fields.configured', serviceConfig.widget.showConfigured)
          }
          if (serviceConfig.widget.showErrored !== undefined) {
            setValue('widget.fields.errored', serviceConfig.widget.showErrored)
          }
        }

        // 设置 Emby 特殊配置（识别原生 emby 小部件）
        if (serviceConfig.widget.type === 'emby' || serviceConfig.widget._nativeEmbyWidget) {
          // 确保界面显示为 emby 类型
          setValue('widget.type', 'emby')

          // 设置官方 Emby 配置选项 - 优先使用 YAML 中的值，然后是默认值
          setValue('widget.enableBlocks', serviceConfig.widget.enableBlocks !== undefined ? serviceConfig.widget.enableBlocks : false)
          setValue('widget.enableNowPlaying', serviceConfig.widget.enableNowPlaying !== undefined ? serviceConfig.widget.enableNowPlaying : true) // 默认为 true
          setValue('widget.enableUser', serviceConfig.widget.enableUser !== undefined ? serviceConfig.widget.enableUser : false)
          setValue('widget.enableMediaControl', serviceConfig.widget.enableMediaControl !== undefined ? serviceConfig.widget.enableMediaControl : true) // 默认为 true
          setValue('widget.showEpisodeNumber', serviceConfig.widget.showEpisodeNumber !== undefined ? serviceConfig.widget.showEpisodeNumber : false)
          setValue('widget.expandOneStreamToTwoRows', serviceConfig.widget.expandOneStreamToTwoRows !== undefined ? serviceConfig.widget.expandOneStreamToTwoRows : true) // 默认为 true

          // 加载 fields 配置 - Emby 支持的字段: ["movies", "series", "episodes", "songs"]
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              // 如果是数组格式，转换为对象格式
              setValue('widget.fields.movies', serviceConfig.widget.fields.includes('movies'))
              setValue('widget.fields.series', serviceConfig.widget.fields.includes('series'))
              setValue('widget.fields.episodes', serviceConfig.widget.fields.includes('episodes'))
              setValue('widget.fields.songs', serviceConfig.widget.fields.includes('songs'))
            } else if (typeof serviceConfig.widget.fields === 'object') {
              // 对象格式，直接设置
              setValue('widget.fields.movies', serviceConfig.widget.fields.movies || false)
              setValue('widget.fields.series', serviceConfig.widget.fields.series || false)
              setValue('widget.fields.episodes', serviceConfig.widget.fields.episodes || false)
              setValue('widget.fields.songs', serviceConfig.widget.fields.songs || false)
            }
          }

          // 加载旧的显示配置（兼容性）
          if (serviceConfig.widget.showMovies !== undefined) {
            setValue('widget.fields.movies', serviceConfig.widget.showMovies)
          }
          if (serviceConfig.widget.showSeries !== undefined) {
            setValue('widget.fields.series', serviceConfig.widget.showSeries)
          }
          if (serviceConfig.widget.showEpisodes !== undefined) {
            setValue('widget.fields.episodes', serviceConfig.widget.showEpisodes)
          }
          if (serviceConfig.widget.showSongs !== undefined) {
            setValue('widget.fields.songs', serviceConfig.widget.showSongs)
          }

          // 兼容旧版本配置 - 如果存在旧的 show* 配置，转换为新格式
          if (serviceConfig.widget.showLibraries || serviceConfig.widget.showActiveUsers ||
            serviceConfig.widget.showNowPlaying || serviceConfig.widget.showRecentlyAdded ||
            serviceConfig.widget.showServerInfo || serviceConfig.widget.showTranscoding) {
            // 如果有任何显示选项被启用，则启用 enableBlocks
            setValue('widget.enableBlocks', true)
            // 如果显示正在播放，则启用 enableNowPlaying
            if (serviceConfig.widget.showNowPlaying) {
              setValue('widget.enableNowPlaying', true)
            }
          }

          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 30)
        }

        // 设置 Jellyfin 特殊配置
        if (serviceConfig.widget.type === 'jellyfin') {
          // 设置官方 Jellyfin 配置选项 - 优先使用 YAML 中的值，然后是默认值
          setValue('widget.enableBlocks', serviceConfig.widget.enableBlocks !== undefined ? serviceConfig.widget.enableBlocks : false)
          setValue('widget.enableNowPlaying', serviceConfig.widget.enableNowPlaying !== undefined ? serviceConfig.widget.enableNowPlaying : true) // 默认为 true
          setValue('widget.enableUser', serviceConfig.widget.enableUser !== undefined ? serviceConfig.widget.enableUser : false)
          setValue('widget.enableMediaControl', serviceConfig.widget.enableMediaControl !== undefined ? serviceConfig.widget.enableMediaControl : true) // 默认为 true
          setValue('widget.showEpisodeNumber', serviceConfig.widget.showEpisodeNumber !== undefined ? serviceConfig.widget.showEpisodeNumber : false)
          setValue('widget.expandOneStreamToTwoRows', serviceConfig.widget.expandOneStreamToTwoRows !== undefined ? serviceConfig.widget.expandOneStreamToTwoRows : true) // 默认为 true

          // 加载 fields 配置 - Jellyfin 支持的字段: ["movies", "series", "episodes", "songs"]
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              // 如果是数组格式，转换为对象格式
              setValue('widget.fields.movies', serviceConfig.widget.fields.includes('movies'))
              setValue('widget.fields.series', serviceConfig.widget.fields.includes('series'))
              setValue('widget.fields.episodes', serviceConfig.widget.fields.includes('episodes'))
              setValue('widget.fields.songs', serviceConfig.widget.fields.includes('songs'))
            } else if (typeof serviceConfig.widget.fields === 'object') {
              // 对象格式，直接设置
              setValue('widget.fields.movies', serviceConfig.widget.fields.movies || false)
              setValue('widget.fields.series', serviceConfig.widget.fields.series || false)
              setValue('widget.fields.episodes', serviceConfig.widget.fields.episodes || false)
              setValue('widget.fields.songs', serviceConfig.widget.fields.songs || false)
            }
          }

          // 加载旧的显示配置（兼容性）
          if (serviceConfig.widget.showMovies !== undefined) {
            setValue('widget.fields.movies', serviceConfig.widget.showMovies)
          }
          if (serviceConfig.widget.showSeries !== undefined) {
            setValue('widget.fields.series', serviceConfig.widget.showSeries)
          }
          if (serviceConfig.widget.showEpisodes !== undefined) {
            setValue('widget.fields.episodes', serviceConfig.widget.showEpisodes)
          }
          if (serviceConfig.widget.showSongs !== undefined) {
            setValue('widget.fields.songs', serviceConfig.widget.showSongs)
          }

          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 30)
        }

        // 设置 Plex 特殊配置 - 按照官方文档
        if (serviceConfig.widget.type === 'plex') {
          // 加载字段配置 - 支持数组格式和对象格式
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              // 如果是数组格式，转换为对象格式
              setValue('widget.fields.streams', serviceConfig.widget.fields.includes('streams'))
              setValue('widget.fields.albums', serviceConfig.widget.fields.includes('albums'))
              setValue('widget.fields.movies', serviceConfig.widget.fields.includes('movies'))
              setValue('widget.fields.tv', serviceConfig.widget.fields.includes('tv'))
            } else if (typeof serviceConfig.widget.fields === 'object') {
              // 对象格式，直接设置
              setValue('widget.fields.streams', serviceConfig.widget.fields.streams || false)
              setValue('widget.fields.albums', serviceConfig.widget.fields.albums || false)
              setValue('widget.fields.movies', serviceConfig.widget.fields.movies || false)
              setValue('widget.fields.tv', serviceConfig.widget.fields.tv || false)
            }
          } else {
            // 默认配置 - 启用 streams 字段
            setValue('widget.fields.streams', true)
            setValue('widget.fields.albums', false)
            setValue('widget.fields.movies', false)
            setValue('widget.fields.tv', false)
          }

          // 加载旧的显示配置（兼容性）
          if (serviceConfig.widget.showStreams !== undefined) {
            setValue('widget.fields.streams', serviceConfig.widget.showStreams)
          }
          if (serviceConfig.widget.showAlbums !== undefined) {
            setValue('widget.fields.albums', serviceConfig.widget.showAlbums)
          }
          if (serviceConfig.widget.showMovies !== undefined) {
            setValue('widget.fields.movies', serviceConfig.widget.showMovies)
          }
          if (serviceConfig.widget.showTv !== undefined) {
            setValue('widget.fields.tv', serviceConfig.widget.showTv)
          }
        }

        // 设置 Sonarr/Radarr 系列特殊配置
        if (['sonarr', 'radarr', 'lidarr', 'readarr', 'prowlarr', 'bazarr', 'jellyseerr'].includes(serviceConfig.widget.type)) {
          setValue('widget.showQueue', serviceConfig.widget.showQueue || false)
          setValue('widget.showWanted', serviceConfig.widget.showWanted || false)
          setValue('widget.showCutoff', serviceConfig.widget.showCutoff || false)
          setValue('widget.showActivity', serviceConfig.widget.showActivity || false)
          setValue('widget.showErrors', serviceConfig.widget.showErrors || false)
          setValue('widget.showWarnings', serviceConfig.widget.showWarnings || false)
          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 30)

          // 为 Prowlarr 加载字段配置
          if (serviceConfig.widget.type === 'prowlarr') {
            if (serviceConfig.widget.fields) {
              if (Array.isArray(serviceConfig.widget.fields)) {
                serviceConfig.widget.fields.forEach(field => {
                  setValue(`widget.fields.${field}`, true)
                })
              }
            }
          }

          // 为 Radarr 加载字段配置
          if (serviceConfig.widget.type === 'radarr') {
            if (serviceConfig.widget.fields) {
              if (Array.isArray(serviceConfig.widget.fields)) {
                serviceConfig.widget.fields.forEach(field => {
                  setValue(`widget.fields.${field}`, true)
                })
              }
            }
            setValue('widget.enableQueue', serviceConfig.widget.enableQueue || false)
          }

          // 为 Sonarr 加载字段配置
          if (serviceConfig.widget.type === 'sonarr') {
            if (serviceConfig.widget.fields) {
              if (Array.isArray(serviceConfig.widget.fields)) {
                serviceConfig.widget.fields.forEach(field => {
                  setValue(`widget.fields.${field}`, true)
                })
              }
            }
            setValue('widget.enableQueue', serviceConfig.widget.enableQueue || false)
          }

          // 为 Jellyseerr 加载字段配置
          if (serviceConfig.widget.type === 'jellyseerr') {
            if (serviceConfig.widget.fields) {
              if (Array.isArray(serviceConfig.widget.fields)) {
                serviceConfig.widget.fields.forEach(field => {
                  setValue(`widget.fields.${field}`, true)
                })
              }
            }
          }
        }

        // 设置 qBittorrent 特殊配置
        if (serviceConfig.widget.type === 'qbittorrent') {
          setValue('widget.showLeech', serviceConfig.widget.showLeech || false)
          setValue('widget.showDownload', serviceConfig.widget.showDownload || false)
          setValue('widget.showSeed', serviceConfig.widget.showSeed || false)
          setValue('widget.showUpload', serviceConfig.widget.showUpload || false)
          setValue('widget.enableLeechProgress', serviceConfig.widget.enableLeechProgress || false)
          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 30)
        }

        // 设置 Transmission 特殊配置
        if (serviceConfig.widget.type === 'transmission') {
          setValue('widget.showLeech', serviceConfig.widget.showLeech || false)
          setValue('widget.showDownload', serviceConfig.widget.showDownload || false)
          setValue('widget.showSeed', serviceConfig.widget.showSeed || false)
          setValue('widget.showUpload', serviceConfig.widget.showUpload || false)
          setValue('widget.rpcUrl', serviceConfig.widget.rpcUrl || '/transmission/')
          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 30)
        }

        // 设置 Pi-hole 特殊配置
        if (serviceConfig.widget.type === 'pihole') {
          setValue('widget.showQueries', serviceConfig.widget.showQueries || false)
          setValue('widget.showBlocked', serviceConfig.widget.showBlocked || false)
          setValue('widget.showPercent', serviceConfig.widget.showPercent || false)
          setValue('widget.showGravity', serviceConfig.widget.showGravity || false)
          setValue('widget.showStatus', serviceConfig.widget.showStatus || false)
          setValue('widget.showClients', serviceConfig.widget.showClients || false)
          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 30)
        }

        // 设置 Bilibili 未读消息相关配置（已禁用功能）
        if (serviceConfig.widget.type === 'bilibili' || serviceConfig.widget._bilibiliWidget) {
          // 强制禁用未读消息功能
          setValue('widget.enableUnread', false)
        }

        // 设置 Synology Disk Station 特殊配置
        if (serviceConfig.widget.type === 'diskstation') {
          setValue('widget.volume', serviceConfig.widget.volume || '')

          // 加载字段配置 - 支持数组格式和对象格式
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              // 如果是数组格式，转换为对象格式
              setValue('widget.fields.uptime', serviceConfig.widget.fields.includes('uptime'))
              setValue('widget.fields.volumeAvailable', serviceConfig.widget.fields.includes('volumeAvailable'))
              setValue('widget.fields.cpu', serviceConfig.widget.fields.includes('resources.cpu'))
              setValue('widget.fields.memory', serviceConfig.widget.fields.includes('resources.mem'))
            } else if (typeof serviceConfig.widget.fields === 'object') {
              // 对象格式，直接设置
              setValue('widget.fields.uptime', serviceConfig.widget.fields.uptime || false)
              setValue('widget.fields.volumeAvailable', serviceConfig.widget.fields.volumeAvailable || false)
              setValue('widget.fields.cpu', serviceConfig.widget.fields.cpu || false)
              setValue('widget.fields.memory', serviceConfig.widget.fields.memory || false)
            }
          } else {
            // 默认配置 - 全部启用
            setValue('widget.fields.uptime', true)
            setValue('widget.fields.volumeAvailable', true)
            setValue('widget.fields.cpu', true)
            setValue('widget.fields.memory', true)
          }

          // 加载旧的显示配置（兼容性）
          if (serviceConfig.widget.showUptime !== undefined) {
            setValue('widget.fields.uptime', serviceConfig.widget.showUptime)
          }
          if (serviceConfig.widget.showVolumeAvailable !== undefined) {
            setValue('widget.fields.volumeAvailable', serviceConfig.widget.showVolumeAvailable)
          }
          if (serviceConfig.widget.showCpu !== undefined) {
            setValue('widget.fields.cpu', serviceConfig.widget.showCpu)
          }
          if (serviceConfig.widget.showMemory !== undefined) {
            setValue('widget.fields.memory', serviceConfig.widget.showMemory)
          }
        }

        // 设置 Bilibili 特殊配置（识别 customapi 类型的 bilibili widget）
        if (serviceConfig.widget.type === 'bilibili' || serviceConfig.widget._bilibiliWidget) {
          // 如果是 customapi 类型但标记为 bilibili widget，设置类型为 bilibili 用于界面显示
          if (serviceConfig.widget.type === 'customapi' && serviceConfig.widget._bilibiliWidget) {
            setValue('widget.type', 'bilibili')
          }
          setValue('widget.videoId', serviceConfig.widget.videoId || '')
          setValue('widget.apiBaseUrl', serviceConfig.widget.apiBaseUrl || '')
          setValue('widget.showView', serviceConfig.widget.showView || false)
          setValue('widget.showLike', serviceConfig.widget.showLike || false)
          setValue('widget.showFavorite', serviceConfig.widget.showFavorite || false)
          setValue('widget.showCoin', serviceConfig.widget.showCoin || false)
          setValue('widget.showShare', serviceConfig.widget.showShare || false)
          setValue('widget.showReply', serviceConfig.widget.showReply || false)
          setValue('widget.showDanmaku', serviceConfig.widget.showDanmaku || false)
          setValue('widget.showDislike', serviceConfig.widget.showDislike || false)
          // 将毫秒转换为分钟显示
          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? Math.round(serviceConfig.widget.refreshInterval / 60 / 1000) : 30)
          setValue('widget.displayMode', serviceConfig.widget.displayMode || serviceConfig.widget.display || 'block')
        }

        // 设置 Navidrome 特殊配置
        if (serviceConfig.widget.type === 'navidrome') {
          setValue('widget.user', serviceConfig.widget.user || '')
          setValue('widget.token', serviceConfig.widget.token || '')
          setValue('widget.salt', serviceConfig.widget.salt || '')
        }

        // 设置 Gitea 特殊配置
        if (serviceConfig.widget.type === 'gitea') {
          // 加载 fields 配置
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              serviceConfig.widget.fields.forEach(field => {
                setValue(`widget.fields.${field}`, true)
              })
            }
          }
        }

        // 设置 Gitlab 特殊配置
        if (serviceConfig.widget.type === 'gitlab') {
          // 加载 user_id
          setValue('widget.user_id', serviceConfig.widget.user_id || '')

          // 加载 fields 配置
          if (serviceConfig.widget.fields) {
            if (Array.isArray(serviceConfig.widget.fields)) {
              serviceConfig.widget.fields.forEach(field => {
                setValue(`widget.fields.${field}`, true)
              })
            }
          }
        }

        // 设置 Homepage Config统计特殊配置
        if (serviceConfig.widget.type === 'homepage-stats' || serviceConfig.widget._homepageStatsWidget) {
          // 确保界面显示为 homepage-stats 类型
          setValue('widget.type', 'homepage-stats')

          setValue('widget.homepageUrl', serviceConfig.widget.homepageUrl || '')
          setValue('widget.homepagePassword', serviceConfig.widget.homepagePassword || '')
          setValue('widget.refreshInterval', serviceConfig.widget.refreshInterval ? serviceConfig.widget.refreshInterval / 1000 : 60)
          setValue('widget.showServices', serviceConfig.widget.showServices === true)
          setValue('widget.showWidgets', serviceConfig.widget.showWidgets === true)
          setValue('widget.showBookmarks', serviceConfig.widget.showBookmarks === true)
          setValue('widget.showGroups', serviceConfig.widget.showGroups === true)
          setValue('widget.showBackups', serviceConfig.widget.showBackups === true)
          setValue('widget.display', serviceConfig.widget.display || 'block')
        }

        // 设置映射
        if (serviceConfig.widget.mappings) {
          setWidgetMappings(serviceConfig.widget.mappings)
        }
      }

      setSelectedGroup(groupName)
    } else if (isOpen) {
      // 新建服务
      reset()
      setWidgetMappings([])
      setCustomFields([])
      setSelectedGroup(groupName)

      // 新建服务时，默认不设置小部件类型（保持为"无小部件"）
      // 用户可以根据需要手动选择小部件类型
    }
  }, [isOpen, service, groupName, setValue, reset])

  // 监听小部件类型变化，为新建的服务设置默认值
  useEffect(() => {
    if (!service) { // 只在新建服务时设置默认值（service 为 null）
      if (watchedWidgetType === 'emby' || watchedWidgetType === 'jellyfin') {
        setValue('widget.enableBlocks', false)
        setValue('widget.enableNowPlaying', true) // 默认启用
        setValue('widget.enableUser', false)
        setValue('widget.enableMediaControl', true) // 默认启用
        setValue('widget.showEpisodeNumber', false)
        setValue('widget.expandOneStreamToTwoRows', true) // 默认启用
        setValue('widget.refreshInterval', 30) // 默认 30 秒
        // 不设置 fields 默认值，让用户自己选择
      } else if (watchedWidgetType === 'homepage-stats') {
        // 为Homepage Config统计设置默认值
        setValue('widget.showServices', true)
        setValue('widget.showWidgets', true)
        setValue('widget.showBookmarks', true)
        setValue('widget.showGroups', true)
        setValue('widget.showBackups', true)
        setValue('widget.refreshInterval', 60) // 默认 60 秒
        setValue('widget.display', 'block')
      }
    }
  }, [watchedWidgetType, service, setValue])

  const onSubmit = (data) => {
    // 为特殊小部件生成默认名称（如果未提供）
    let serviceName = data.name

    // 为 Glances 小部件生成默认名称
    if (data.widget?.type === 'glances' && !serviceName) {
      // 根据选择的指标生成默认名称
      const selectedFields = []

      // 添加预设指标选择
      if (data.widget.selectedMetric) {
        selectedFields.push(data.widget.selectedMetric)
      }

      // 添加自定义字段到选择列表
      customFields.forEach(customField => {
        if (customField.field && customField.field.trim()) {
          selectedFields.push(customField.field.trim())
        }
      })

      if (selectedFields.length > 0) {
        const fieldNames = {
          cpu: 'CPU',
          memory: '内存',
          disk: '磁盘',
          network: '网络',
          load: '负载',
          uptime: '运行时间'
        }

        // 优先使用自定义字段的显示名称
        let primaryFieldName = fieldNames[selectedFields[0]]

        // 如果是自定义字段，尝试使用用户定义的显示名称
        if (!primaryFieldName) {
          const customField = customFields.find(cf => cf.field.trim() === selectedFields[0])
          primaryFieldName = customField?.label || selectedFields[0]
        }

        serviceName = `${primaryFieldName}监控`
      } else {
        serviceName = '系统监控'
      }
    }

    // 为 Bilibili 小部件生成默认名称
    if (data.widget?.type === 'bilibili' && !serviceName) {
      // 优先使用提取到的title作为服务名称
      if (data.widget.extractedTitle) {
        serviceName = data.widget.extractedTitle
      } else if (data.widget.videoId) {
        serviceName = `Bilibili ${data.widget.videoId}`
      } else {
        serviceName = 'Bilibili 视频监控'
      }
    }

    const serviceData = {
      [serviceName]: {
        icon: data.icon,
        href: data.href || undefined, // 如果为空则不添加 href
        description: data.description,
        target: data.target || '_blank'
      }
    }

    // 添加 Ping 配置（只有在启用且提供了 URL 的情况下）
    if (data.enablePing && data.ping && data.ping.trim()) {
      serviceData[serviceName].ping = data.ping.trim()

      // 添加 ping 方法（默认为 http）
      if (data.pingMethod && data.pingMethod !== 'http') {
        serviceData[serviceName].pingMethod = data.pingMethod
      }

      // 添加超时时间（如果不是默认值）
      const timeout = parseInt(data.pingTimeout) || 5000
      if (timeout !== 5000) {
        serviceData[serviceName].pingTimeout = timeout
      }
    }

    // 添加小部件配置
    if (data.widget?.type) {
      // 如果是Homepage统计，转换为customapi类型以兼容Homepage
      if (data.widget.type === 'homepage-stats') {
        // 调试信息：打印复选框的值
        console.log('🔍 Homepage Config统计复选框值:', {
          showServices: data.widget.showServices,
          showWidgets: data.widget.showWidgets,
          showBookmarks: data.widget.showBookmarks,
          showGroups: data.widget.showGroups,
          showBackups: data.widget.showBackups
        })

        const mappings = []
        if (data.widget.showWidgets === true) {
          mappings.push({ field: 'data.local.widgets', label: '部件', format: 'number' })
        }
        if (data.widget.showServices === true) {
          mappings.push({ field: 'data.local.services', label: '服务', format: 'number' })
        }
        if (data.widget.showBookmarks === true) {
          mappings.push({ field: 'data.local.bookmarks', label: '书签', format: 'number' })
        }
        if (data.widget.showGroups === true) {
          mappings.push({ field: 'data.local.totalGroups', label: '分组', format: 'number' })
        }
        if (data.widget.showBackups === true) {
          mappings.push({ field: 'data.local.backups', label: '备份', format: 'number' })
        }

        console.log('🔍 生成的mappings:', mappings)

        serviceData[serviceName].widget = {
          type: 'customapi',
          url: `${window.location.protocol}//${window.location.host}/api/homepage-stats`,
          refreshInterval: (data.widget.refreshInterval || 60) * 1000,
          method: 'GET',
          display: data.widget.display || 'block',
          ...(data.widget.homepageUrl && {
            headers: {
              'X-Homepage-URL': data.widget.homepageUrl,
              ...(data.widget.homepagePassword && { 'X-Homepage-Password': data.widget.homepagePassword })
            }
          }),
          mappings: mappings
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget._homepageStatsWidget = true
        serviceData[serviceName].widget.homepageUrl = data.widget.homepageUrl || ''
        serviceData[serviceName].widget.homepagePassword = data.widget.homepagePassword || ''
        serviceData[serviceName].widget.showServices = data.widget.showServices === true
        serviceData[serviceName].widget.showWidgets = data.widget.showWidgets === true
        serviceData[serviceName].widget.showBookmarks = data.widget.showBookmarks === true
        serviceData[serviceName].widget.showGroups = data.widget.showGroups === true
        serviceData[serviceName].widget.showBackups = data.widget.showBackups === true
      } else {
        serviceData[serviceName].widget = {
          type: data.widget.type,
          url: data.widget.url,
        }
      }

      // 根据小部件类型添加特定字段
      if (data.widget.key) serviceData[serviceName].widget.key = data.widget.key
      if (data.widget.username) serviceData[serviceName].widget.username = data.widget.username
      if (data.widget.password) serviceData[serviceName].widget.password = data.widget.password
      if (data.widget.env) serviceData[serviceName].widget.env = parseInt(data.widget.env)

      // Emby 小部件特殊配置 - 使用 Homepage 官方标准配置
      if (data.widget.type === 'emby') {
        // Homepage Emby 小部件的官方标准配置
        // 参考: https://gethomepage.dev/widgets/services/emby/

        // 确保必要的字段存在
        if (!data.widget.url) {
          alert('Emby 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.key) {
          alert('Emby 小部件需要配置 API Key')
          return
        }

        // 使用 Homepage 官方的 Emby 小部件配置格式
        serviceData[serviceName].widget = {
          type: 'emby',
          url: data.widget.url,
          key: data.widget.key
        }

        // 可选配置 - 使用官方参数名
        if (data.widget.username) {
          serviceData[serviceName].widget.username = data.widget.username
        }
        if (data.widget.password) {
          serviceData[serviceName].widget.password = data.widget.password
        }

        // 官方支持的配置选项
        if (data.widget.enableBlocks !== undefined) {
          serviceData[serviceName].widget.enableBlocks = data.widget.enableBlocks
        }
        if (data.widget.enableNowPlaying !== undefined) {
          serviceData[serviceName].widget.enableNowPlaying = data.widget.enableNowPlaying
        }
        if (data.widget.enableUser !== undefined) {
          serviceData[serviceName].widget.enableUser = data.widget.enableUser
        }
        if (data.widget.enableMediaControl !== undefined) {
          serviceData[serviceName].widget.enableMediaControl = data.widget.enableMediaControl
        }
        if (data.widget.showEpisodeNumber !== undefined) {
          serviceData[serviceName].widget.showEpisodeNumber = data.widget.showEpisodeNumber
        }
        if (data.widget.expandOneStreamToTwoRows !== undefined) {
          serviceData[serviceName].widget.expandOneStreamToTwoRows = data.widget.expandOneStreamToTwoRows
        }

        // 处理 fields 配置 - Emby 支持的字段: ["movies", "series", "episodes", "songs"]
        const fields = []
        if (data.widget.fields?.movies) fields.push('movies')
        if (data.widget.fields?.series) fields.push('series')
        if (data.widget.fields?.episodes) fields.push('episodes')
        if (data.widget.fields?.songs) fields.push('songs')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        // 刷新间隔配置（如果需要）
        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 保存配置用于编辑时回显
        serviceData[serviceName].widget.enableBlocks = data.widget.enableBlocks || false
        serviceData[serviceName].widget.enableNowPlaying = data.widget.enableNowPlaying !== false // 默认为 true
        serviceData[serviceName].widget.enableUser = data.widget.enableUser || false
        serviceData[serviceName].widget.enableMediaControl = data.widget.enableMediaControl !== false // 默认为 true
        serviceData[serviceName].widget.showEpisodeNumber = data.widget.showEpisodeNumber || false
        serviceData[serviceName].widget.expandOneStreamToTwoRows = data.widget.expandOneStreamToTwoRows !== false // 默认为 true

        // 保存 fields 配置用于编辑时回显
        serviceData[serviceName].widget.showMovies = data.widget.fields?.movies || false
        serviceData[serviceName].widget.showSeries = data.widget.fields?.series || false
        serviceData[serviceName].widget.showEpisodes = data.widget.fields?.episodes || false
        serviceData[serviceName].widget.showSongs = data.widget.fields?.songs || false

        // 标记这是原生 Emby 小部件（用于编辑时识别）
        serviceData[serviceName].widget._nativeEmbyWidget = true
      }

      // Jellyfin 小部件特殊配置
      if (data.widget.type === 'jellyfin') {
        if (!data.widget.url) {
          alert('Jellyfin 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.key) {
          alert('Jellyfin 小部件需要配置 API Key')
          return
        }

        const hasSelectedFields = data.widget.fields?.movies || data.widget.fields?.series ||
          data.widget.fields?.episodes || data.widget.fields?.songs

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的内容')
          return
        }

        const fields = []
        if (data.widget.fields?.movies) fields.push('movies')
        if (data.widget.fields?.series) fields.push('series')
        if (data.widget.fields?.episodes) fields.push('episodes')
        if (data.widget.fields?.songs) fields.push('songs')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 保存 Jellyfin 复选框配置
        if (data.widget.enableBlocks !== undefined) {
          serviceData[serviceName].widget.enableBlocks = data.widget.enableBlocks
        }
        if (data.widget.enableNowPlaying !== undefined) {
          serviceData[serviceName].widget.enableNowPlaying = data.widget.enableNowPlaying
        }
        if (data.widget.enableUser !== undefined) {
          serviceData[serviceName].widget.enableUser = data.widget.enableUser
        }
        if (data.widget.enableMediaControl !== undefined) {
          serviceData[serviceName].widget.enableMediaControl = data.widget.enableMediaControl
        }
        if (data.widget.showEpisodeNumber !== undefined) {
          serviceData[serviceName].widget.showEpisodeNumber = data.widget.showEpisodeNumber
        }
        if (data.widget.expandOneStreamToTwoRows !== undefined) {
          serviceData[serviceName].widget.expandOneStreamToTwoRows = data.widget.expandOneStreamToTwoRows
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.showMovies = data.widget.fields?.movies || false
        serviceData[serviceName].widget.showSeries = data.widget.fields?.series || false
        serviceData[serviceName].widget.showEpisodes = data.widget.fields?.episodes || false
        serviceData[serviceName].widget.showSongs = data.widget.fields?.songs || false
      }

      // Plex 小部件特殊配置 - 使用 mappings 格式
      if (data.widget.type === 'plex') {
        if (!data.widget.url) {
          alert('Plex 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.key) {
          alert('Plex 小部件需要配置 Token')
          return
        }

        // 根据实际使用经验，Plex 支持的字段为: ["streams", "albums", "movies", "tv"]
        const hasSelectedFields = data.widget.fields?.streams || data.widget.fields?.albums ||
          data.widget.fields?.movies || data.widget.fields?.tv

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的字段')
          return
        }

        // 构建 mappings 配置 - Plex 使用 mappings 格式
        const mappings = []
        if (data.widget.fields?.streams) {
          mappings.push({ field: 'streams', label: '流媒体', format: 'number' })
        }
        if (data.widget.fields?.albums) {
          mappings.push({ field: 'albums', label: '音乐专辑', format: 'number' })
        }
        if (data.widget.fields?.movies) {
          mappings.push({ field: 'movies', label: '电影', format: 'number' })
        }
        if (data.widget.fields?.tv) {
          mappings.push({ field: 'tv', label: '电视剧', format: 'number' })
        }

        if (mappings.length > 0) {
          serviceData[serviceName].widget.mappings = mappings
        }

        // 保存用于界面显示的配置（不会出现在最终 YAML 中）
        serviceData[serviceName].widget.showStreams = data.widget.fields?.streams || false
        serviceData[serviceName].widget.showAlbums = data.widget.fields?.albums || false
        serviceData[serviceName].widget.showMovies = data.widget.fields?.movies || false
        serviceData[serviceName].widget.showTv = data.widget.fields?.tv || false
      }

      // Sonarr/Radarr 系列小部件特殊配置
      if (['sonarr', 'radarr', 'lidarr', 'readarr', 'prowlarr', 'bazarr', 'jellyseerr'].includes(data.widget.type)) {
        if (!data.widget.url) {
          alert(`${data.widget.type} 小部件需要配置服务器 URL`)
          return
        }

        if (!data.widget.key) {
          alert(`${data.widget.type} 小部件需要配置 API Key`)
          return
        }

        // 检查是否至少选择了一个字段 - 根据小部件类型使用不同的验证逻辑
        let hasSelectedFields = false

        if (['prowlarr', 'radarr', 'sonarr', 'jellyseerr'].includes(data.widget.type)) {
          // 新添加的小部件使用 fields 对象
          if (data.widget.type === 'prowlarr') {
            hasSelectedFields = data.widget.fields?.numberOfGrabs || data.widget.fields?.numberOfQueries ||
              data.widget.fields?.numberOfFailGrabs || data.widget.fields?.numberOfFailQueries
          } else if (data.widget.type === 'radarr') {
            hasSelectedFields = data.widget.fields?.wanted || data.widget.fields?.missing ||
              data.widget.fields?.queued || data.widget.fields?.movies
          } else if (data.widget.type === 'sonarr') {
            hasSelectedFields = data.widget.fields?.wanted || data.widget.fields?.queued ||
              data.widget.fields?.series
          } else if (data.widget.type === 'jellyseerr') {
            hasSelectedFields = data.widget.fields?.pending || data.widget.fields?.approved ||
              data.widget.fields?.available
          }
        } else {
          // 旧的arr系列小部件使用 show* 字段
          hasSelectedFields = data.widget.showQueue || data.widget.showWanted ||
            data.widget.showCutoff || data.widget.showActivity ||
            data.widget.showErrors || data.widget.showWarnings
        }

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的内容')
          return
        }

        // 处理字段配置 - 根据小部件类型使用不同的处理逻辑
        if (['prowlarr', 'radarr', 'sonarr', 'jellyseerr'].includes(data.widget.type)) {
          // 新添加的小部件字段处理在各自的配置块中处理
        } else {
          // 旧的arr系列小部件使用 show* 字段
          const fields = []
          if (data.widget.showQueue) fields.push('queue')
          if (data.widget.showWanted) fields.push('wanted')
          if (data.widget.showCutoff) fields.push('cutoff')
          if (data.widget.showActivity) fields.push('activity')
          if (data.widget.showErrors) fields.push('errors')
          if (data.widget.showWarnings) fields.push('warnings')

          if (fields.length > 0) {
            serviceData[serviceName].widget.fields = fields
          }

          // 保存原始配置用于编辑时回显
          serviceData[serviceName].widget.showQueue = data.widget.showQueue || false
          serviceData[serviceName].widget.showWanted = data.widget.showWanted || false
          serviceData[serviceName].widget.showCutoff = data.widget.showCutoff || false
          serviceData[serviceName].widget.showActivity = data.widget.showActivity || false
          serviceData[serviceName].widget.showErrors = data.widget.showErrors || false
          serviceData[serviceName].widget.showWarnings = data.widget.showWarnings || false
        }

        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 为 Prowlarr 添加字段配置
        if (data.widget.type === 'prowlarr') {
          const fields = []
          if (data.widget.fields?.numberOfGrabs) fields.push('numberOfGrabs')
          if (data.widget.fields?.numberOfQueries) fields.push('numberOfQueries')
          if (data.widget.fields?.numberOfFailGrabs) fields.push('numberOfFailGrabs')
          if (data.widget.fields?.numberOfFailQueries) fields.push('numberOfFailQueries')
          if (fields.length > 0) {
            serviceData[serviceName].widget.fields = fields
          }
        }

        // 为 Radarr 添加字段配置
        if (data.widget.type === 'radarr') {
          const fields = []
          if (data.widget.fields?.wanted) fields.push('wanted')
          if (data.widget.fields?.missing) fields.push('missing')
          if (data.widget.fields?.queued) fields.push('queued')
          if (data.widget.fields?.movies) fields.push('movies')
          if (fields.length > 0) {
            serviceData[serviceName].widget.fields = fields
          }
          if (data.widget.enableQueue) {
            serviceData[serviceName].widget.enableQueue = true
          }
        }

        // 为 Sonarr 添加字段配置
        if (data.widget.type === 'sonarr') {
          const fields = []
          if (data.widget.fields?.wanted) fields.push('wanted')
          if (data.widget.fields?.queued) fields.push('queued')
          if (data.widget.fields?.series) fields.push('series')
          if (fields.length > 0) {
            serviceData[serviceName].widget.fields = fields
          }
          if (data.widget.enableQueue) {
            serviceData[serviceName].widget.enableQueue = true
          }
        }

        // 为 Jellyseerr 添加字段配置
        if (data.widget.type === 'jellyseerr') {
          const fields = []
          if (data.widget.fields?.pending) fields.push('pending')
          if (data.widget.fields?.approved) fields.push('approved')
          if (data.widget.fields?.available) fields.push('available')
          if (fields.length > 0) {
            serviceData[serviceName].widget.fields = fields
          }
        }
      }

      // qBittorrent 小部件特殊配置
      if (data.widget.type === 'qbittorrent') {
        if (!data.widget.url) {
          alert('qBittorrent 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.username || !data.widget.password) {
          alert('qBittorrent 小部件需要配置用户名和密码')
          return
        }

        const hasSelectedFields = data.widget.showLeech || data.widget.showDownload ||
          data.widget.showSeed || data.widget.showUpload

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的内容')
          return
        }

        const fields = []
        if (data.widget.showLeech) fields.push('leech')
        if (data.widget.showDownload) fields.push('download')
        if (data.widget.showSeed) fields.push('seed')
        if (data.widget.showUpload) fields.push('upload')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        // 添加 enableLeechProgress 配置
        if (data.widget.enableLeechProgress) {
          serviceData[serviceName].widget.enableLeechProgress = data.widget.enableLeechProgress
        }

        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.showLeech = data.widget.showLeech || false
        serviceData[serviceName].widget.showDownload = data.widget.showDownload || false
        serviceData[serviceName].widget.showSeed = data.widget.showSeed || false
        serviceData[serviceName].widget.showUpload = data.widget.showUpload || false
        serviceData[serviceName].widget.enableLeechProgress = data.widget.enableLeechProgress || false
      }

      // Transmission 小部件特殊配置
      if (data.widget.type === 'transmission') {
        if (!data.widget.url) {
          alert('Transmission 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.username || !data.widget.password) {
          alert('Transmission 小部件需要配置用户名和密码')
          return
        }

        // 根据官方文档，允许字段 ["leech", "download", "seed", "upload"]
        const hasSelectedFields = data.widget.showLeech || data.widget.showDownload ||
          data.widget.showSeed || data.widget.showUpload

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的内容')
          return
        }

        const fields = []
        if (data.widget.showLeech) fields.push('leech')
        if (data.widget.showDownload) fields.push('download')
        if (data.widget.showSeed) fields.push('seed')
        if (data.widget.showUpload) fields.push('upload')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        if (data.widget.rpcUrl && data.widget.rpcUrl.trim()) {
          serviceData[serviceName].widget.rpcUrl = data.widget.rpcUrl.trim()
        }

        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.showLeech = data.widget.showLeech || false
        serviceData[serviceName].widget.showDownload = data.widget.showDownload || false
        serviceData[serviceName].widget.showSeed = data.widget.showSeed || false
        serviceData[serviceName].widget.showUpload = data.widget.showUpload || false
        serviceData[serviceName].widget.rpcUrl = data.widget.rpcUrl || ''
      }

      // Pi-hole 小部件特殊配置
      if (data.widget.type === 'pihole') {
        if (!data.widget.url) {
          alert('Pi-hole 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.key) {
          alert('Pi-hole 小部件需要配置 API Key')
          return
        }

        const hasSelectedFields = data.widget.showQueries || data.widget.showBlocked ||
          data.widget.showPercent || data.widget.showGravity ||
          data.widget.showStatus || data.widget.showClients

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的内容')
          return
        }

        const fields = []
        if (data.widget.showQueries) fields.push('queries')
        if (data.widget.showBlocked) fields.push('blocked')
        if (data.widget.showPercent) fields.push('percent')
        if (data.widget.showGravity) fields.push('gravity')
        if (data.widget.showStatus) fields.push('status')
        if (data.widget.showClients) fields.push('clients')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.showQueries = data.widget.showQueries || false
        serviceData[serviceName].widget.showBlocked = data.widget.showBlocked || false
        serviceData[serviceName].widget.showPercent = data.widget.showPercent || false
        serviceData[serviceName].widget.showGravity = data.widget.showGravity || false
        serviceData[serviceName].widget.showStatus = data.widget.showStatus || false
        serviceData[serviceName].widget.showClients = data.widget.showClients || false
      }

      // Synology Disk Station 小部件特殊配置
      if (data.widget.type === 'diskstation') {
        if (!data.widget.url) {
          alert('Synology Disk Station 小部件需要配置 DSM URL')
          return
        }

        if (!data.widget.username || !data.widget.password) {
          alert('Synology Disk Station 小部件需要配置用户名和密码')
          return
        }

        // 检查是否至少选择了一个字段
        const hasSelectedFields = data.widget.fields?.uptime || data.widget.fields?.volumeAvailable ||
          data.widget.fields?.cpu || data.widget.fields?.memory

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的监控字段')
          return
        }

        // 构建 fields 数组用于 Homepage 配置
        const fields = []
        if (data.widget.fields?.uptime) fields.push('uptime')
        if (data.widget.fields?.volumeAvailable) fields.push('volumeAvailable')
        if (data.widget.fields?.cpu) fields.push('resources.cpu')
        if (data.widget.fields?.memory) fields.push('resources.mem')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        // 添加存储卷配置
        if (data.widget.volume && data.widget.volume.trim()) {
          serviceData[serviceName].widget.volume = data.widget.volume.trim()
        }

        // 保存用于界面显示的配置（不会出现在最终 YAML 中）
        serviceData[serviceName].widget.showUptime = data.widget.fields?.uptime || false
        serviceData[serviceName].widget.showVolumeAvailable = data.widget.fields?.volumeAvailable || false
        serviceData[serviceName].widget.showCpu = data.widget.fields?.cpu || false
        serviceData[serviceName].widget.showMemory = data.widget.fields?.memory || false
      }

      // Glances 特殊配置
      if (data.widget.type === 'glances') {
        // 收集选中的显示字段
        const selectedFields = []

        // 添加预设指标选择
        if (data.widget.selectedMetric) {
          selectedFields.push(data.widget.selectedMetric)
        }

        // 添加自定义字段
        customFields.forEach(customField => {
          if (customField.field && customField.field.trim()) {
            selectedFields.push(customField.field.trim())
          }
        })

        // 保存选中的指标（用于编辑时回显）
        if (data.widget.selectedMetric) {
          serviceData[serviceName].widget.selectedMetric = data.widget.selectedMetric
        }

        // Homepage Glances 小部件配置
        // 参考: https://gethomepage.dev/widgets/services/glances/

        // Homepage Glances 只支持单个 metric，不支持 fields 数组
        if (selectedFields.length > 0) {
          let metric = selectedFields[0] // 默认使用第一个选中的指标

          // 根据指标类型和配置构建 metric 字符串
          if (metric === 'network' && data.widget.interface) {
            metric = `network:${data.widget.interface}`
          } else if (metric === 'disk' && data.widget.disk) {
            metric = `fs:${data.widget.disk}`
          }

          serviceData[serviceName].widget.metric = metric
        }

        // 添加其他 Glances 配置
        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 添加版本配置
        const version = data.widget.version || 4
        serviceData[serviceName].widget.version = version

        // 保存自定义字段
        if (customFields.length > 0) {
          serviceData[serviceName].widget.customFields = customFields.filter(cf => cf.field && cf.field.trim())
        }
      }

      // AdGuard Home 特殊配置
      if (data.widget.type === 'adguard') {
        if (!data.widget.url) {
          alert('AdGuard Home 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.username || !data.widget.password) {
          alert('AdGuard Home 小部件需要配置用户名和密码')
          return
        }

        // 检查是否至少选择了一个字段
        const hasSelectedFields = data.widget.showQueries || data.widget.showBlocked ||
          data.widget.showFiltered || data.widget.showLatency

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的内容')
          return
        }

        // 将 type 改为 adguard（Homepage 官方标准）
        serviceData[serviceName].widget.type = 'adguard'

        const fields = []
        if (data.widget.showQueries) fields.push('queries')
        if (data.widget.showBlocked) fields.push('blocked')
        if (data.widget.showFiltered) fields.push('filtered')
        if (data.widget.showLatency) fields.push('latency')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.showQueries = data.widget.showQueries || false
        serviceData[serviceName].widget.showBlocked = data.widget.showBlocked || false
        serviceData[serviceName].widget.showFiltered = data.widget.showFiltered || false
        serviceData[serviceName].widget.showLatency = data.widget.showLatency || false
      }

      // Jackett 特殊配置
      if (data.widget.type === 'jackett') {
        if (!data.widget.url) {
          alert('Jackett 小部件需要配置服务器 URL')
          return
        }

        // 检查是否至少选择了一个字段
        const hasSelectedFields = data.widget.fields?.configured || data.widget.fields?.errored

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的内容')
          return
        }

        // 将 type 改为 jackett（Homepage 官方标准）
        serviceData[serviceName].widget.type = 'jackett'

        const fields = []
        if (data.widget.fields?.configured) fields.push('configured')
        if (data.widget.fields?.errored) fields.push('errored')

        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }

        // 添加密码配置（如果设置了）
        if (data.widget.password) {
          serviceData[serviceName].widget.password = data.widget.password
        }

        if (data.widget.refreshInterval) {
          const intervalSeconds = parseInt(data.widget.refreshInterval)
          if (intervalSeconds && intervalSeconds > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalSeconds * 1000
          }
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.showConfigured = data.widget.fields?.configured || false
        serviceData[serviceName].widget.showErrored = data.widget.fields?.errored || false
      }

      // Mikrotik 特殊配置
      if (data.widget.type === 'mikrotik') {
        // 收集选中的字段
        const fields = []
        if (data.widget.fields?.uptime) fields.push('uptime')
        if (data.widget.fields?.cpuLoad) fields.push('cpuLoad')
        if (data.widget.fields?.memoryUsed) fields.push('memoryUsed')
        if (data.widget.fields?.numberOfLeases) fields.push('numberOfLeases')

        // 添加 fields 配置
        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }
      }

      // Tautulli 小部件特殊配置
      if (data.widget.type === 'tautulli') {
        if (!data.widget.url) {
          alert('Tautulli 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.key) {
          alert('Tautulli 小部件需要配置 API Key')
          return
        }

        // 添加 Tautulli 特有的配置选项
        if (data.widget.enableUser !== undefined) {
          serviceData[serviceName].widget.enableUser = data.widget.enableUser
        }
        if (data.widget.showEpisodeNumber !== undefined) {
          serviceData[serviceName].widget.showEpisodeNumber = data.widget.showEpisodeNumber
        }
        if (data.widget.expandOneStreamToTwoRows !== undefined) {
          serviceData[serviceName].widget.expandOneStreamToTwoRows = data.widget.expandOneStreamToTwoRows
        }

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.enableUser = data.widget.enableUser || false
        serviceData[serviceName].widget.showEpisodeNumber = data.widget.showEpisodeNumber || false
        serviceData[serviceName].widget.expandOneStreamToTwoRows = data.widget.expandOneStreamToTwoRows !== false // 默认为 true
      }

      // Navidrome 小部件特殊配置
        if (data.widget.type === 'navidrome') {
          if (!data.widget.url) {
            alert('Navidrome 小部件需要配置服务器 URL')
            return
          }

          if (!data.widget.user) {
            alert('Navidrome 小部件需要配置用户名')
            return
          }

          if (!data.widget.token) {
            alert('Navidrome 小部件需要配置 Token')
            return
          }

          if (!data.widget.salt) {
            alert('Navidrome 小部件需要配置 Salt')
            return
          }

          // 添加 Navidrome 特殊字段
          serviceData[serviceName].widget.user = data.widget.user
          serviceData[serviceName].widget.token = data.widget.token
          serviceData[serviceName].widget.salt = data.widget.salt
        }

        // Gitea 小部件特殊配置
        if (data.widget.type === 'gitea') {
          if (!data.widget.url) {
            alert('Gitea 小部件需要配置服务器 URL')
            return
          }

          if (!data.widget.key) {
            alert('Gitea 小部件需要配置 API Token')
            return
          }

          // 收集选中的字段
          const fields = []
          if (data.widget.fields?.repositories !== false) fields.push('repositories')
          if (data.widget.fields?.notifications) fields.push('notifications')
          if (data.widget.fields?.issues) fields.push('issues')
          if (data.widget.fields?.pulls) fields.push('pulls')

          // 如果有选中的字段，添加到配置中
          if (fields.length > 0) {
            serviceData[serviceName].widget.fields = fields
          }
        }

      // Gitlab 小部件特殊配置
      if (data.widget.type === 'gitlab') {
        if (!data.widget.url) {
          alert('Gitlab 小部件需要配置服务器 URL')
          return
        }

        if (!data.widget.key) {
          alert('Gitlab 小部件需要配置个人访问令牌')
          return
        }

        if (!data.widget.user_id) {
          alert('Gitlab 小部件需要配置用户 ID')
          return
        }

        // 添加 user_id
        serviceData[serviceName].widget.user_id = parseInt(data.widget.user_id)

        // 收集选中的字段
        const fields = []
        if (data.widget.fields?.events !== false) fields.push('events')
        if (data.widget.fields?.issues) fields.push('issues')
        if (data.widget.fields?.merges) fields.push('merges')
        if (data.widget.fields?.projects) fields.push('projects')

        // 如果有选中的字段，添加到配置中
        if (fields.length > 0) {
          serviceData[serviceName].widget.fields = fields
        }
      }

      // Bilibili 特殊配置 - 使用 customapi 类型以兼容 Homepage
      if (data.widget.type === 'bilibili') {
        // 更改为 customapi 类型以兼容 Homepage
        serviceData[serviceName].widget.type = 'customapi'

        // 构建 API URL - 使用用户配置的API基础地址或自动检测
        let apiBaseUrl = data.widget.apiBaseUrl
        if (!apiBaseUrl) {
          apiBaseUrl = getManagerBaseUrl()
        }
        apiBaseUrl = apiBaseUrl.replace(/\/$/, '')

        // 未读消息功能已禁用，只支持视频数据接口
        if (data.widget.videoId) {
          serviceData[serviceName].widget.url = `${apiBaseUrl}/api/bilibili/video/${data.widget.videoId}`
        }

        // 保留这些字段用于编辑时回显，但不会出现在最终的Homepage配置中
        if (data.widget.videoId) serviceData[serviceName].widget.videoId = data.widget.videoId
        serviceData[serviceName].widget.apiBaseUrl = data.widget.apiBaseUrl || ''

        // 检查是否至少选择了一个字段（未读消息功能已禁用）
        const hasSelectedFields = data.widget.showView || data.widget.showLike ||
          data.widget.showFavorite || data.widget.showCoin ||
          data.widget.showShare || data.widget.showReply ||
          data.widget.showDanmaku || data.widget.showDislike

        if (!hasSelectedFields) {
          alert('请至少选择一个要显示的数据字段（观看数、点赞数、收藏数、投币数、分享数、评论数、弹幕数或点踩数）')
          return
        }

        // customapi widget不需要fields数组，只需要mappings

        // 添加mappings配置 - Homepage customapi需要这个来正确显示数据（未读消息功能已禁用）
        const mappings = []
        if (data.widget.showView) mappings.push({ field: 'view', label: '观看', format: 'number' })
        if (data.widget.showLike) mappings.push({ field: 'like', label: '点赞', format: 'number' })
        if (data.widget.showFavorite) mappings.push({ field: 'favorite', label: '收藏', format: 'number' })
        if (data.widget.showCoin) mappings.push({ field: 'coin', label: '投币', format: 'number' })
        if (data.widget.showShare) mappings.push({ field: 'share', label: '分享', format: 'number' })
        if (data.widget.showReply) mappings.push({ field: 'reply', label: '评论', format: 'number' })
        if (data.widget.showDanmaku) mappings.push({ field: 'danmaku', label: '弹幕', format: 'number' })
        if (data.widget.showDislike) mappings.push({ field: 'dislike', label: '点踩', format: 'number' })

        // mappings应该与fields保持一致，不需要额外检查

        // 添加method参数确保请求方式正确
        serviceData[serviceName].widget.method = 'GET'

        // 添加显示布局方式配置
        if (data.widget.displayMode && data.widget.displayMode !== 'block') {
          serviceData[serviceName].widget.display = data.widget.displayMode
        }

        serviceData[serviceName].widget.mappings = mappings

        // 保存原始配置用于编辑时回显
        serviceData[serviceName].widget.showView = data.widget.showView || false
        serviceData[serviceName].widget.showLike = data.widget.showLike || false
        serviceData[serviceName].widget.showFavorite = data.widget.showFavorite || false
        serviceData[serviceName].widget.showCoin = data.widget.showCoin || false
        serviceData[serviceName].widget.showShare = data.widget.showShare || false
        serviceData[serviceName].widget.showReply = data.widget.showReply || false
        serviceData[serviceName].widget.showDanmaku = data.widget.showDanmaku || false
        serviceData[serviceName].widget.showDislike = data.widget.showDislike || false
        serviceData[serviceName].widget.displayMode = data.widget.displayMode || 'block'

        // 刷新间隔配置（分钟转换为毫秒）
        if (data.widget.refreshInterval) {
          const intervalMinutes = parseInt(data.widget.refreshInterval)
          if (intervalMinutes && intervalMinutes > 0) {
            serviceData[serviceName].widget.refreshInterval = intervalMinutes * 60 * 1000 // 分钟转毫秒
          }
        } else {
          // 默认30分钟
          serviceData[serviceName].widget.refreshInterval = 30 * 60 * 1000
        }

        // 标记这是一个 bilibili widget（用于编辑时识别）
        serviceData[serviceName].widget._bilibiliWidget = true
      } else if (!['tautulli', 'emby', 'jellyfin', 'plex', 'sonarr', 'radarr', 'lidarr', 'readarr', 'prowlarr', 'bazarr', 'jellyseerr', 'qbittorrent', 'pihole', 'diskstation', 'glances', 'homepage-stats', 'navidrome'].includes(data.widget.type)) {
        // 只对通用widget（非特殊处理的widget）添加用户自定义映射
        if (widgetMappings.length > 0) {
          serviceData[serviceName].widget.mappings = widgetMappings
        }
      }
    }

    onSave(selectedGroup, serviceData)
    onClose()
  }

  const addMapping = () => {
    setWidgetMappings([...widgetMappings, { field: '', label: '', format: 'text' }])
  }

  const updateMapping = (index, field, value) => {
    const newMappings = [...widgetMappings]
    newMappings[index][field] = value
    setWidgetMappings(newMappings)
  }

  const removeMapping = (index) => {
    setWidgetMappings(widgetMappings.filter((_, i) => i !== index))
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={onClose} />

          <div className="inline-block w-full max-w-3xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">
                {service ? '编辑服务' : '添加服务'}
              </h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* 基本信息 */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-200">基本信息</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      服务名称 {watchedWidgetType !== 'glances' && watchedWidgetType !== 'bilibili' && watchedWidgetType !== 'homepage-stats' && '*'}
                    </label>
                    <input
                      {...register('name', {
                        required: (watchedWidgetType !== 'glances' && watchedWidgetType !== 'bilibili' && watchedWidgetType !== 'homepage-stats') ? '服务名称是必填的' : false
                      })}
                      className="input"
                      placeholder={
                        watchedWidgetType === 'glances' ? '例如：服务器监控' :
                          watchedWidgetType === 'bilibili' ? '例如：我的视频数据' :
                            watchedWidgetType === 'homepage-stats' ? '例如：Homepage Config统计' :
                              '例如：Plex'
                      }
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      服务组
                    </label>
                    <select
                      value={selectedGroup}
                      onChange={(e) => setSelectedGroup(e.target.value)}
                      className="select"
                    >
                      {availableGroups.map(group => (
                        <option key={group} value={group}>{group}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    访问地址 {watchedWidgetType !== 'glances' && watchedWidgetType !== 'bilibili' && watchedWidgetType !== 'homepage-stats' && '*'}
                  </label>
                  <div className="flex gap-2">
                    <ProtocolSelector
                      value={watch('href')?.startsWith('https://') ? 'https://' : 'http://'}
                      onChange={(protocol) => {
                        const currentHref = watch('href') || ''
                        const urlWithoutProtocol = currentHref.replace(/^https?:\/\//, '')
                        setValue('href', protocol + urlWithoutProtocol)
                      }}
                      className="w-28"
                    />
                    <input
                      {...register('href', {
                        required: (watchedWidgetType !== 'glances' && watchedWidgetType !== 'bilibili' && watchedWidgetType !== 'homepage-stats') ? '访问地址是必填的' : false
                      })}
                      className="input flex-1"
                      placeholder={
                        watchedWidgetType === 'glances' ? '可选：服务访问地址' :
                          watchedWidgetType === 'bilibili' ? '可选：视频页面地址' :
                            watchedWidgetType === 'homepage-stats' ? '可选：Homepage访问地址' :
                              'localhost:8080'
                      }
                      onFocus={(e) => {
                        // 如果输入框为空，自动填入 http://
                        if (!e.target.value) {
                          setValue('href', 'http://')
                        }
                      }}
                    />
                  </div>
                  {errors.href && (
                    <p className="mt-1 text-sm text-red-400">{errors.href.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      图标
                    </label>
                    <div className="flex gap-2 items-start">
                      <div className="flex-1">
                        <div className="flex gap-2">
                          <input
                            {...register('icon')}
                            className="input flex-1"
                            placeholder="plex.png 或 si-plex 或 https://... 或 icons/myicon.svg"
                          />
                          <button
                            type="button"
                            onClick={handleExtractIcon}
                            disabled={isExtractingIcon || !watchedHref}
                            className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center"
                            title="从访问地址自动提取图标"
                          >
                            <ArrowPathIcon
                              className={`h-4 w-4 ${isExtractingIcon ? 'animate-spin' : ''}`}
                            />
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowIconSearch(true)}
                            className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center"
                            title="搜索在线图标"
                          >
                            <MagnifyingGlassIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowLocalIcons(true)}
                            className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center"
                            title="本地图标库"
                          >
                            <FolderIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">
                          支持 Dashboard Icons、Simple Icons、Material Icons、完整URL 或本地图标
                        </p>
                      </div>

                      {/* 图标预览 */}
                      {watch('icon') && (
                        <div className="flex-shrink-0 ml-2">
                          <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center border border-gray-600">
                            {getIconPreviewUrl(watch('icon')) ? (
                              <img
                                src={getIconPreviewUrl(watch('icon'))}
                                alt="图标预览"
                                className="w-8 h-8 object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none'
                                  e.target.nextSibling.style.display = 'block'
                                }}
                              />
                            ) : null}
                            <span
                              className="text-xs text-gray-400 hidden"
                              style={{ display: getIconPreviewUrl(watch('icon')) ? 'none' : 'block' }}
                            >
                              无预览
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      打开方式
                    </label>
                    <select {...register('target')} className="select">
                      <option value="_blank">新标签页</option>
                      <option value="_self">当前页面</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    描述
                  </label>
                  <input
                    {...register('description')}
                    className="input"
                    placeholder="服务描述"
                  />
                </div>

                {/* Ping 配置 */}
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        {...register('enablePing')}
                        className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                      />
                      <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-xs">🌐</span>
                      </div>
                      <h5 className="font-medium text-blue-200">启用 Ping 连接检测</h5>
                    </label>
                  </div>

                  {/* 折叠内容：只有当勾选启用时才显示 */}
                  {watch('enablePing') && (
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Ping URL *
                          </label>
                          <input
                            {...register('ping', {
                              required: watch('enablePing') ? 'Ping URL 是必填的' : false
                            })}
                            className="input"
                            placeholder="http://localhost:8080"
                          />
                          {errors.ping && (
                            <p className="text-red-500 text-sm mt-1">{errors.ping.message}</p>
                          )}
                          <p className="mt-1 text-xs text-blue-300">
                            用于检测服务连接状态的地址
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            检测方式
                          </label>
                          <select {...register('pingMethod')} className="select">
                            <option value="http">🌐 HTTP 检测</option>
                            <option value="tcp">🔌 TCP 端口检测</option>
                            <option value="icmp">📡 ICMP Ping</option>
                          </select>
                          <p className="mt-1 text-xs text-blue-300">
                            选择适合的检测方式
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            超时时间 (毫秒)
                          </label>
                          <input
                            {...register('pingTimeout', { valueAsNumber: true })}
                            type="number"
                            min="1000"
                            max="30000"
                            className="input"
                            placeholder="5000"
                          />
                          <p className="mt-1 text-xs text-blue-300">
                            默认 5000ms，范围 1000-30000ms
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            🎨 状态样式 (全局)
                          </label>
                          <select
                            value={statusStyle}
                            onChange={(e) => handleStatusStyleChange(e.target.value)}
                            className="select"
                          >
                            <option value="">默认</option>
                            <option value="dot">圆点</option>
                            <option value="basic">基本</option>
                          </select>
                          <p className="mt-1 text-xs text-blue-300">
                            全局设置，影响所有 Ping 状态的显示样式
                          </p>
                        </div>
                      </div>

                      <div className="p-3 bg-blue-950/50 rounded border border-blue-600/20">
                        <h6 className="text-xs font-medium text-blue-300 mb-2">💡 Ping 检测说明</h6>
                        <div className="text-xs text-blue-200 space-y-1">
                          <p>• <strong>HTTP 检测</strong>：通过 HTTP 请求检测服务状态，适用于 Web 服务</p>
                          <p>• <strong>TCP 检测</strong>：检测端口是否可连接，适用于数据库、API 服务</p>
                          <p>• <strong>ICMP Ping</strong>：传统 ping 检测，适用于基础网络连通性</p>
                          <p>• 启用后 Homepage 会在服务卡片上显示连接状态指示器</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 折叠提示 */}
                  {!watch('enablePing') && (
                    <p className="mt-2 text-xs text-blue-300">
                      勾选以启用 Ping 连接检测功能，将在服务卡片上显示连接状态
                    </p>
                  )}
                </div>
              </div>

              {/* 小部件配置 */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-200">小部件配置（可选）</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      小部件类型
                    </label>
                    <select {...register('widget.type')} className="select">
                      <option value="">无小部件</option>

                      {/* 媒体服务器 */}
                      <optgroup label="📺 媒体服务器">
                        <option value="plex">Plex</option>
                        <option value="jellyfin">Jellyfin</option>
                        <option value="emby">Emby</option>
                        <option value="tautulli">Tautulli</option>
                        <option value="navidrome">Navidrome</option>
                        <option value="bilibili">Bilibili 视频数据</option>
                      </optgroup>

                      {/* 媒体管理工具 */}
                      <optgroup label="🎬 媒体管理工具">
                        <option value="jackett">Jackett</option>
                        <option value="prowlarr">Prowlarr</option>
                        <option value="radarr">Radarr</option>
                        <option value="sonarr">Sonarr</option>
                        <option value="jellyseerr">Jellyseerr</option>
                      </optgroup>

                      {/* 下载工具 */}
                      <optgroup label="⬇️ 下载工具">
                        <option value="qbittorrent">qBittorrent</option>
                        <option value="transmission">Transmission</option>
                      </optgroup>

                      {/* 网络工具 */}
                      <optgroup label="🌐 网络工具">
                        <option value="adguard">AdGuard Home</option>
                        <option value="mikrotik">Mikrotik</option>
                      </optgroup>

                      {/* 监控工具 */}
                      <optgroup label="💽 监控工具">
                        <option value="glances">Glances</option>
                        <option value="homepage-stats">Homepage Config统计</option>
                      </optgroup>

                      {/* 存储工具 */}
                      <optgroup label="💾 存储工具">
                        <option value="diskstation">Synology Disk Station</option>
                      </optgroup>

                      {/* 代码仓库 */}
                      <optgroup label="📦 代码仓库">
                        <option value="gitea">Gitea</option>
                        <option value="gitlab">Gitlab</option>
                      </optgroup>

                      {/* 自定义 */}
                      <optgroup label="🔧 自定义">
                        <option value="customapi">自定义 API</option>
                      </optgroup>
                    </select>
                  </div>

                  {/* 只对没有专门配置区域的widget显示通用URL字段 */}
                  {watchedWidgetType && ![
                    // 媒体服务器类
                    'plex', 'jellyfin', 'emby', 'tautulli', 'navidrome', 'bilibili',
                    // 媒体管理工具类
                    'jackett', 'prowlarr', 'radarr', 'sonarr', 'jellyseerr',
                    // 下载工具类
                    'qbittorrent', 'transmission',
                    // 网络工具类
                    'adguard', 'mikrotik',
                    // 监控工具类
                    'glances', 'homepage-stats',
                    // 存储工具类
                    'diskstation',
                    // 代码仓库类
                    'gitea', 'gitlab',
                    // 自定义类
                    'customapi'
                  ].includes(watchedWidgetType) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          小部件 URL
                        </label>
                        <div className="flex gap-2">
                          <input
                            {...register('widget.url')}
                            className="input flex-1"
                            placeholder="http://localhost:8080"
                          />
                        </div>
                        {errors.widget?.url && (
                          <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                        )}
                      </div>
                    )}
                </div>

                {/* 只对没有专门配置区域的widget显示通用API Key和用户名字段 */}
                {watchedWidgetType && ![
                  // 媒体服务器类
                  'plex', 'jellyfin', 'emby', 'tautulli', 'navidrome', 'bilibili',
                  // 媒体管理工具类
                  'jackett', 'prowlarr', 'radarr', 'sonarr', 'jellyseerr',
                  // 下载工具类
                  'qbittorrent', 'transmission',
                  // 网络工具类
                  'adguard', 'mikrotik',
                  // 监控工具类
                  'glances', 'homepage-stats',
                  // 存储工具类
                  'diskstation',
                  // 代码仓库类
                  'gitea', 'gitlab',
                  // 自定义类
                  'customapi'
                ].includes(watchedWidgetType) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          API Key / Token
                        </label>
                        <input
                          {...register('widget.key')}
                          className="input"
                          placeholder="API密钥或令牌"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          用户名
                        </label>
                        <input
                          {...register('widget.username')}
                          className="input"
                          placeholder="用户名"
                        />
                      </div>
                    </div>
                  )}

                {/* 只对没有专门配置区域的widget显示通用密码字段 */}
                {watchedWidgetType && ![
                  // 媒体服务器类
                  'plex', 'jellyfin', 'emby', 'tautulli', 'navidrome', 'bilibili',
                  // 媒体管理工具类
                  'prowlarr', 'radarr', 'sonarr', 'jellyseerr', 'lidarr', 'readarr', 'bazarr',
                  // 下载工具类
                  'qbittorrent', 'transmission',
                  // 网络工具类
                  'adguard', 'mikrotik',
                  // 监控工具类
                  'glances', 'homepage-stats',
                  // 存储工具类
                  'diskstation',
                  // 代码仓库类
                  'gitea', 'gitlab',
                  // 自定义类
                  'customapi'
                ].includes(watchedWidgetType) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          密码
                        </label>
                        <input
                          {...register('widget.password')}
                          type="text"
                          className="input"
                          placeholder="密码"
                        />
                      </div>
                    </div>
                  )}

                {/* Jellyfin 专用配置 */}
                {watchedWidgetType === 'jellyfin' && (
                  <div className="space-y-4">
                    {/* Jellyfin 说明提示 */}
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">ℹ</span>
                        </div>
                        <div className="text-sm text-purple-200">
                          <p className="font-medium mb-1">Jellyfin 媒体服务器小部件说明</p>
                          <p className="text-purple-300 mb-2">
                            根据 Homepage 官方文档配置 Jellyfin 小部件，支持显示媒体库、正在播放内容等信息。
                          </p>
                          <div className="text-xs text-purple-400 space-y-1">
                            <p>• <strong>URL:</strong> Jellyfin 服务器地址 (必填，如: http://localhost:8096)</p>
                            <p>• <strong>API Key:</strong> 在 Jellyfin 设置 → 高级 → API 密钥 中获取</p>
                            <p>• <strong>支持字段:</strong> ["movies", "series", "episodes", "songs"] - v0.6.11+</p>
                            <p>• <strong>默认启用:</strong> enableNowPlaying=true, enableMediaControl=true, expandOneStreamToTwoRows=true</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                        Jellyfin 配置
                      </h4>

                      {/* 连接配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Jellyfin 服务器 URL <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'Jellyfin 服务器地址是必填的' })}
                            className="input"
                            placeholder="http://jellyfin.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            API Key <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.key', { required: 'Jellyfin API 密钥是必填的' })}
                            className="input"
                            placeholder="apikeyapikeyapikeyapikeyapikey"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>

                      {/* 官方 Jellyfin 配置选项 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Jellyfin 小部件配置 <span className="text-xs text-gray-400">(基于官方文档)</span>
                        </label>
                        <div className="space-y-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableBlocks')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">启用块状显示 (enableBlocks)</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableNowPlaying')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示正在播放 (enableNowPlaying)</span>
                            <span className="text-xs text-gray-500">- 默认: true</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableUser')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示用户信息 (enableUser)</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableMediaControl')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">启用媒体控制 (enableMediaControl)</span>
                            <span className="text-xs text-gray-500">- 默认: true</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showEpisodeNumber')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示剧集编号 (showEpisodeNumber)</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.expandOneStreamToTwoRows')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">单流扩展为两行 (expandOneStreamToTwoRows)</span>
                            <span className="text-xs text-gray-500">- 默认: true</span>
                          </label>
                        </div>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(v0.6.11+ 支持，可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.movies')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">电影 (movies)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.series')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">电视剧 (series)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.episodes')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">剧集 (episodes)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.songs')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">歌曲 (songs)</span>
                          </label>
                        </div>
                      </div>

                      {/* 刷新间隔配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="5"
                          className="input"
                          placeholder="30"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          数据刷新间隔，建议 30-60 秒
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://jellyfin.host.or.ip'
                            const key = watch('widget.key') || 'apikeyapikeyapikeyapikeyapikey'
                            const enableBlocks = watch('widget.enableBlocks')
                            const enableNowPlaying = watch('widget.enableNowPlaying')
                            const enableUser = watch('widget.enableUser')
                            const enableMediaControl = watch('widget.enableMediaControl')
                            const showEpisodeNumber = watch('widget.showEpisodeNumber')
                            const expandOneStreamToTwoRows = watch('widget.expandOneStreamToTwoRows')
                            const refreshInterval = watch('widget.refreshInterval')

                            // 字段选择
                            const moviesField = watch('widget.fields.movies')
                            const seriesField = watch('widget.fields.series')
                            const episodesField = watch('widget.fields.episodes')
                            const songsField = watch('widget.fields.songs')

                            let config = `widget:
  type: jellyfin
  url: ${url}
  key: ${key}`

                            // 只显示非默认值的配置
                            if (enableBlocks) {
                              config += `
  enableBlocks: true`
                            }
                            if (enableNowPlaying === false) {
                              config += `
  enableNowPlaying: false`
                            }
                            if (enableUser) {
                              config += `
  enableUser: true`
                            }
                            if (enableMediaControl === false) {
                              config += `
  enableMediaControl: false`
                            }
                            if (showEpisodeNumber) {
                              config += `
  showEpisodeNumber: true`
                            }
                            if (expandOneStreamToTwoRows === false) {
                              config += `
  expandOneStreamToTwoRows: false`
                            }

                            // 字段配置
                            const fields = []
                            if (moviesField) fields.push('movies')
                            if (seriesField) fields.push('series')
                            if (episodesField) fields.push('episodes')
                            if (songsField) fields.push('songs')

                            if (fields.length > 0) {
                              config += `
  fields: ["${fields.join('", "')}"]`
                            }

                            if (refreshInterval && refreshInterval !== 30) {
                              config += `
  refreshInterval: ${refreshInterval}000`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Emby 专用配置 */}
                {watchedWidgetType === 'emby' && (
                  <div className="space-y-4">
                    {/* Emby 说明提示 */}
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">ℹ</span>
                        </div>
                        <div className="text-sm text-green-200">
                          <p className="font-medium mb-1">Emby 媒体服务器小部件 (Homepage 原生)</p>
                          <p className="text-green-300 mb-2">
                            使用 Homepage 官方标准的 Emby 小部件配置，支持显示媒体库、正在播放内容等信息。
                          </p>
                          <div className="text-xs text-green-400 space-y-1">
                            <p>• <strong>URL:</strong> Emby 服务器地址 (必填，如: http://192.168.1.100:8096)</p>
                            <p>• <strong>API Key:</strong> Emby API 密钥 (必填)</p>
                            <p>• <strong>获取 API Key:</strong> Emby 管理界面 → 设置 → 高级 → API 密钥</p>
                            <p>• <strong>支持字段:</strong> ["movies", "series", "episodes", "songs"]</p>
                            <p>• <strong>配置格式:</strong> 严格按照 Homepage 官方文档的 Emby 小部件标准</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Emby 配置
                      </h4>

                      {/* 连接配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Emby 服务器 URL <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'Emby 服务器地址是必填的' })}
                            className="input"
                            placeholder="http://emby.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            API Key <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.key', { required: 'Emby API 密钥是必填的' })}
                            className="input"
                            placeholder="apikeyapikeyapikeyapikeyapikey"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>

                      {/* 官方 Emby 配置选项 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Emby 小部件配置 <span className="text-xs text-gray-400">(基于官方文档)</span>
                        </label>
                        <div className="space-y-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableBlocks')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">启用块状显示 (enableBlocks)</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableNowPlaying')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示正在播放 (enableNowPlaying)</span>
                            <span className="text-xs text-gray-500">- 默认: true</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableUser')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示用户信息 (enableUser)</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableMediaControl')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">启用媒体控制 (enableMediaControl)</span>
                            <span className="text-xs text-gray-500">- 默认: true</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showEpisodeNumber')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示剧集编号 (showEpisodeNumber)</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.expandOneStreamToTwoRows')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">单流扩展为两行 (expandOneStreamToTwoRows)</span>
                            <span className="text-xs text-gray-500">- 默认: true</span>
                          </label>
                        </div>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(v0.6.11+ 支持，可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.movies')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">电影 (movies)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.series')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">电视剧 (series)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.episodes')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">剧集 (episodes)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.songs')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">歌曲 (songs)</span>
                          </label>
                        </div>
                      </div>

                      {/* 刷新间隔配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="5"
                          className="input"
                          placeholder="30"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          数据刷新间隔，建议 30-60 秒
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://emby.host.or.ip'
                            const key = watch('widget.key') || 'apikeyapikeyapikeyapikeyapikey'
                            const enableBlocks = watch('widget.enableBlocks')
                            const enableNowPlaying = watch('widget.enableNowPlaying')
                            const enableUser = watch('widget.enableUser')
                            const enableMediaControl = watch('widget.enableMediaControl')
                            const showEpisodeNumber = watch('widget.showEpisodeNumber')
                            const expandOneStreamToTwoRows = watch('widget.expandOneStreamToTwoRows')
                            const refreshInterval = watch('widget.refreshInterval')

                            // 字段选择
                            const moviesField = watch('widget.fields.movies')
                            const seriesField = watch('widget.fields.series')
                            const episodesField = watch('widget.fields.episodes')
                            const songsField = watch('widget.fields.songs')

                            let config = `widget:
  type: emby
  url: ${url}
  key: ${key}`

                            // 只显示非默认值的配置
                            if (enableBlocks) {
                              config += `
  enableBlocks: true`
                            }
                            if (enableNowPlaying === false) {
                              config += `
  enableNowPlaying: false`
                            }
                            if (enableUser) {
                              config += `
  enableUser: true`
                            }
                            if (enableMediaControl === false) {
                              config += `
  enableMediaControl: false`
                            }
                            if (showEpisodeNumber) {
                              config += `
  showEpisodeNumber: true`
                            }
                            if (expandOneStreamToTwoRows === false) {
                              config += `
  expandOneStreamToTwoRows: false`
                            }

                            // 字段配置
                            const fields = []
                            if (moviesField) fields.push('movies')
                            if (seriesField) fields.push('series')
                            if (episodesField) fields.push('episodes')
                            if (songsField) fields.push('songs')

                            if (fields.length > 0) {
                              config += `
  fields: [\"${fields.join('\", \"')}\"]`
                            }

                            if (refreshInterval && refreshInterval !== 30) {
                              config += `
  refreshInterval: ${refreshInterval}000`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Plex 专用配置 */}
                {watchedWidgetType === 'plex' && (
                  <div className="space-y-4">
                    {/* Plex 说明提示 */}
                    <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">🎬</span>
                        </div>
                        <div className="text-sm text-orange-200">
                          <p className="font-medium mb-1">Plex 媒体服务器小部件 (官方标准)</p>
                          <p className="text-orange-300 mb-2">
                            根据 Homepage 官方文档配置 Plex 小部件，支持显示流媒体、音乐、电影和电视剧统计。
                          </p>
                          <div className="text-xs text-orange-400 space-y-1">
                            <p>• <strong>URL:</strong> Plex 服务器地址 (必填，如: http://localhost:32400)</p>
                            <p>• <strong>Token:</strong> Plex API Token (必填)</p>
                            <p>• <strong>获取 Token:</strong> 参考 https://www.plexopedia.com/plex-media-server/general/plex-token/</p>
                            <p>• <strong>支持字段:</strong> ["streams", "albums", "movies", "tv"] - 官方文档标准</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                        Plex 连接配置
                      </h4>

                      {/* 连接配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Plex 服务器 URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Plex 服务器地址是必填的' })}
                            className="input"
                            placeholder="http://localhost:32400"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Plex Token *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Plex Token 是必填的' })}
                            className="input"
                            placeholder="your-plex-token"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(官方支持字段，可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.streams')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">流媒体 (streams)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.albums')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">音乐专辑 (albums)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.movies')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">电影 (movies)</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.tv')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">电视剧 (tv)</span>
                          </label>
                        </div>
                        <p className="mt-2 text-xs text-gray-400">
                          使用 mappings 格式配置，参考：<a href="https://gethomepage.dev/widgets/services/plex/" target="_blank" className="text-orange-400 hover:text-orange-300">Plex Widget Documentation</a>
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览 (使用 mappings 格式)</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://localhost:32400'
                            const key = watch('widget.key') || 'your-plex-token'
                            const showStreams = watch('widget.fields.streams')
                            const showAlbums = watch('widget.fields.albums')
                            const showMovies = watch('widget.fields.movies')
                            const showTv = watch('widget.fields.tv')

                            const mappings = []
                            if (showStreams) mappings.push({ field: 'streams', label: '流媒体', format: 'number' })
                            if (showAlbums) mappings.push({ field: 'albums', label: '音乐专辑', format: 'number' })
                            if (showMovies) mappings.push({ field: 'movies', label: '电影', format: 'number' })
                            if (showTv) mappings.push({ field: 'tv', label: '电视剧', format: 'number' })

                            let config = `widget:
  type: plex
  url: ${url}
  key: ${key}`

                            if (mappings.length > 0) {
                              config += `
  mappings:`
                              mappings.forEach(mapping => {
                                config += `
    - field: ${mapping.field}
      label: ${mapping.label}
      format: ${mapping.format}`
                              })
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}


                {/* qBittorrent 专用配置 */}
                {watchedWidgetType === 'qbittorrent' && (
                  <div className="space-y-4">
                    {/* qBittorrent 说明提示 */}
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">ℹ</span>
                        </div>
                        <div className="text-sm text-red-200">
                          <p className="font-medium mb-1">qBittorrent 下载器小部件说明</p>
                          <p className="text-red-300 mb-2">
                            显示 qBittorrent 的下载统计和上传下载信息。
                          </p>
                          <div className="text-xs text-red-400 space-y-1">
                            <p>• <strong>URL:</strong> qBittorrent Web UI 地址 (如: http://localhost:8080)</p>
                            <p>• <strong>用户名/密码:</strong> 必需！qBittorrent 需要登录认证</p>
                            <p>• <strong>默认账户:</strong> admin/adminpass (首次安装)</p>
                            <p>• <strong>注意:</strong> 确保在 qBittorrent 设置中启用了 Web UI</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        qBittorrent 连接配置
                      </h4>

                      {/* 连接配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            qBittorrent Web UI URL <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'qBittorrent URL 是必填的' })}
                            className="input"
                            placeholder="http://localhost:8080"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            qBittorrent Web UI 访问地址
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.username', { required: 'qBittorrent 用户名是必填的' })}
                            className="input"
                            placeholder="admin"
                          />
                          {errors.widget?.username && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.username.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Web UI 登录用户名
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            密码 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.password', { required: 'qBittorrent 密码是必填的' })}
                            type="text"
                            className="input"
                            placeholder="adminpass"
                          />
                          {errors.widget?.password && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.password.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Web UI 登录密码
                          </p>
                        </div>
                      </div>

                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        qBittorrent 显示配置
                      </h4>

                      {/* 显示内容选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示内容 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showLeech')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载中种子数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showDownload')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载速度</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showSeed')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">做种中种子数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showUpload')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">上传速度</span>
                          </label>
                        </div>
                      </div>

                      {/* 下载进度配置 */}
                      <div className="mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.enableLeechProgress')}
                            className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">启用下载进度显示</span>
                        </label>
                        <p className="mt-1 text-xs text-gray-400">
                          显示正在下载的种子的详细进度信息
                        </p>
                      </div>

                      {/* 刷新间隔配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="5"
                          className="input"
                          placeholder="30"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          数据刷新间隔，建议 30-60 秒
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://localhost:8080'
                            const username = watch('widget.username') || 'admin'
                            const password = watch('widget.password') || 'adminpass'
                            const showLeech = watch('widget.showLeech')
                            const showDownload = watch('widget.showDownload')
                            const showSeed = watch('widget.showSeed')
                            const showUpload = watch('widget.showUpload')
                            const enableLeechProgress = watch('widget.enableLeechProgress')
                            const refreshInterval = watch('widget.refreshInterval') || 30

                            const fields = []
                            if (showLeech) fields.push('leech')
                            if (showDownload) fields.push('download')
                            if (showSeed) fields.push('seed')
                            if (showUpload) fields.push('upload')

                            let config = `widget:
  type: qbittorrent
  url: ${url}
  username: ${username}
  password: ${password}`

                            if (enableLeechProgress) {
                              config += `
  enableLeechProgress: true`
                            }

                            if (refreshInterval !== 30) {
                              config += `
  refreshInterval: ${refreshInterval}000`
                            }

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Transmission 专用配置 */}
                {watchedWidgetType === 'transmission' && (
                  <div className="space-y-4">
                    {/* Transmission 说明提示 */}
                    <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">T</span>
                        </div>
                        <div className="text-sm text-cyan-200">
                          <p className="font-medium mb-1">Transmission BT 客户端小部件说明</p>
                          <p className="text-cyan-300 mb-2">
                            显示种子下载状态、传输速度和队列信息。
                          </p>
                          <div className="text-xs text-cyan-400 space-y-1">
                            <p>• <strong>URL:</strong> Transmission Web 界面地址</p>
                            <p>• <strong>认证:</strong> Web 界面用户名和密码</p>
                            <p>• <strong>RPC URL:</strong> 可选，匹配 settings.json 中的 rpc-url 值</p>
                            <p>• <strong>显示字段:</strong> leech(下载中), download(下载速度), seed(做种中), upload(上传速度)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-cyan-500 rounded-full mr-2"></span>
                        Transmission 认证设置
                      </h4>

                      {/* 连接配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Transmission 服务器 URL <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'Transmission 服务器地址是必填的' })}
                            className="input"
                            placeholder="http://transmission.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.username', { required: 'Transmission 用户名是必填的' })}
                            className="input"
                            placeholder="transmission 登录用户名"
                          />
                          {errors.widget?.username && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.username.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            密码 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.password', { required: 'Transmission 密码是必填的' })}
                            type="text"
                            className="input"
                            placeholder="transmission 登录密码"
                          />
                          {errors.widget?.password && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.password.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          RPC URL <span className="text-xs text-gray-400">(可选)</span>
                        </label>
                        <input
                          {...register('widget.rpcUrl')}
                          className="input"
                          placeholder="/transmission/ (默认值，匹配 settings.json 中的 rpc-url)"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          RPC 路径，通常为 /transmission/ 或自定义路径
                        </p>
                      </div>

                      {/* 刷新间隔 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒) <span className="text-xs text-gray-400">(可选，默认 30)</span>
                        </label>
                        <input
                          {...register('widget.refreshInterval')}
                          type="number"
                          min="5"
                          className="input"
                          placeholder="30"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          控制小部件数据刷新频率。建议 ≥ 5 秒。
                        </p>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示内容 <span className="text-xs text-gray-400">(最多4个字段)</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showLeech')}
                              className="border-gray-600 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载中种子数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showDownload')}
                              className="border-gray-600 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载速度</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showSeed')}
                              className="border-gray-600 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">做种中种子数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showUpload')}
                              className="border-gray-600 text-cyan-600 focus:ring-cyan-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">上传速度</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://transmission.host.or.ip'
                            const username = watch('widget.username') || 'username'
                            const password = watch('widget.password') || 'password'
                            const rpcUrl = watch('widget.rpcUrl')
                            const refreshInterval = watch('widget.refreshInterval') || 30
                            const showLeech = watch('widget.showLeech')
                            const showDownload = watch('widget.showDownload')
                            const showSeed = watch('widget.showSeed')
                            const showUpload = watch('widget.showUpload')

                            const fields = []
                            if (showLeech) fields.push('leech')
                            if (showDownload) fields.push('download')
                            if (showSeed) fields.push('seed')
                            if (showUpload) fields.push('upload')

                            let config = `widget:
  type: transmission
  url: ${url}
  username: ${username}
  password: ${password}`

                            if (rpcUrl) {
                              config += `
  rpcUrl: ${rpcUrl}`
                            }

                            if (refreshInterval && parseInt(refreshInterval) !== 30) {
                              config += `
  refreshInterval: ${parseInt(refreshInterval)}000`
                            }

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Deluge 专用配置 */}
                {watchedWidgetType === 'deluge' && (
                  <div className="space-y-4">
                    {/* Deluge 说明提示 */}
                    <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">D</span>
                        </div>
                        <div className="text-sm text-orange-200">
                          <p className="font-medium mb-1">Deluge BT 客户端小部件说明</p>
                          <p className="text-orange-300 mb-2">
                            显示 Deluge 的下载状态和传输统计。
                          </p>
                          <div className="text-xs text-orange-400 space-y-1">
                            <p>• <strong>URL:</strong> Deluge Web 界面地址</p>
                            <p>• <strong>密码:</strong> Web 界面密码 (与登录密码相同)</p>
                            <p>• <strong>显示字段:</strong> leech(下载中), download(下载速度), seed(做种中), upload(上传速度)</p>
                            <p>• <strong>下载进度:</strong> 可选择启用下载进度显示</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                        Deluge 认证设置
                      </h4>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Web 界面密码 <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register('widget.password', { required: 'Deluge Web 界面密码是必填的' })}
                          type="text"
                          className="input"
                          placeholder="deluge web 界面密码"
                        />
                        {errors.widget?.password && (
                          <p className="text-red-400 text-xs mt-1">{errors.widget.password.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">
                          使用与登录 Deluge Web 界面相同的密码
                        </p>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示内容 <span className="text-xs text-gray-400">(最多4个字段)</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showLeech')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载中种子数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showDownload')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载速度</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showSeed')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">做种中种子数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showUpload')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">上传速度</span>
                          </label>
                        </div>
                      </div>

                      {/* 下载进度配置 */}
                      <div className="mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.enableLeechProgress')}
                            className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">启用下载进度显示</span>
                        </label>
                        <p className="mt-1 text-xs text-gray-400">
                          显示正在下载的种子的详细进度信息
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://deluge.host.or.ip'
                            const password = watch('widget.password') || 'password'
                            const enableLeechProgress = watch('widget.enableLeechProgress')

                            let config = `widget:
  type: deluge
  url: ${url}
  password: ${password}`

                            if (enableLeechProgress) {
                              config += `
  enableLeechProgress: true`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* ruTorrent 专用配置 */}
                {watchedWidgetType === 'rutorrent' && (
                  <div className="space-y-4">
                    {/* ruTorrent 说明提示 */}
                    <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">R</span>
                        </div>
                        <div className="text-sm text-emerald-200">
                          <p className="font-medium mb-1">ruTorrent Web 客户端小部件说明</p>
                          <p className="text-emerald-300 mb-2">
                            显示 ruTorrent 的活跃种子和传输统计。
                          </p>
                          <div className="text-xs text-emerald-400 space-y-1">
                            <p>• <strong>URL:</strong> ruTorrent Web 界面地址</p>
                            <p>• <strong>认证:</strong> Web 界面用户名和密码 (可选)</p>
                            <p>• <strong>插件要求:</strong> 需要 httprpc 插件 (默认包含)</p>
                            <p>• <strong>显示字段:</strong> active(活跃种子), upload(上传速度), download(下载速度)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                        ruTorrent 认证设置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名 <span className="text-xs text-gray-400">(可选)</span>
                          </label>
                          <input
                            {...register('widget.username')}
                            className="input"
                            placeholder="Web 界面用户名 (如果启用了认证)"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            如果 ruTorrent Web 界面未启用认证，可留空
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            密码 <span className="text-xs text-gray-400">(可选)</span>
                          </label>
                          <input
                            {...register('widget.password')}
                            type="text"
                            className="input"
                            placeholder="Web 界面密码 (如果启用了认证)"
                          />
                        </div>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示内容 <span className="text-xs text-gray-400">(最多3个字段)</span>
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showActive')}
                              className="border-gray-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">活跃种子</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showUpload')}
                              className="border-gray-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">上传速度</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showDownload')}
                              className="border-gray-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载速度</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://rutorrent.host.or.ip'
                            const username = watch('widget.username')
                            const password = watch('widget.password')

                            let config = `widget:
  type: rutorrent
  url: ${url}`

                            if (username) {
                              config += `
  username: ${username}`
                            } else {
                              config += `
  username: false  # 如果没有启用认证`
                            }

                            if (password) {
                              config += `
  password: ${password}`
                            } else {
                              config += `
  password: false  # 如果没有启用认证`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* SABnzbd 专用配置 */}
                {watchedWidgetType === 'sabnzbd' && (
                  <div className="space-y-4">
                    {/* SABnzbd 说明提示 */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">S</span>
                        </div>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">SABnzbd Usenet 客户端小部件说明</p>
                          <p className="text-blue-300 mb-2">
                            显示 SABnzbd 的下载队列、速率和剩余时间。
                          </p>
                          <div className="text-xs text-blue-400 space-y-1">
                            <p>• <strong>URL:</strong> SABnzbd Web 界面地址</p>
                            <p>• <strong>API Key:</strong> 在 Config → General 中获取</p>
                            <p>• <strong>显示字段:</strong> rate(下载速度), queue(队列数量), timeleft(剩余时间)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        SABnzbd API 设置
                      </h4>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          API Key <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register('widget.key', { required: 'SABnzbd API Key 是必填的' })}
                          className="input"
                          placeholder="在 Config → General 中获取的 API Key"
                        />
                        {errors.widget?.key && (
                          <p className="text-red-400 text-xs mt-1">{errors.widget.key.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">
                          在 SABnzbd Web 界面的 Config → General 页面中找到 API Key
                        </p>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示内容 <span className="text-xs text-gray-400">(最多3个字段)</span>
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showRate')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载速度</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showQueue')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">队列数量</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showTimeLeft')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">剩余时间</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://sabnzbd.host.or.ip'
                            const key = watch('widget.key') || 'apikeyapikeyapikeyapikeyapikey'

                            let config = `widget:
  type: sabnzbd
  url: ${url}
  key: ${key}`

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* NZBget 专用配置 */}
                {watchedWidgetType === 'nzbget' && (
                  <div className="space-y-4">
                    {/* NZBget 说明提示 */}
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">N</span>
                        </div>
                        <div className="text-sm text-purple-200">
                          <p className="font-medium mb-1">NZBget Usenet 客户端小部件说明</p>
                          <p className="text-purple-300 mb-2">
                            显示 NZBget 的下载速率、剩余队列和已完成下载。
                          </p>
                          <div className="text-xs text-purple-400 space-y-1">
                            <p>• <strong>URL:</strong> NZBget Web 界面地址</p>
                            <p>• <strong>认证:</strong> ControlUsername 和 ControlPassword (HTTP Basic Auth)</p>
                            <p>• <strong>显示字段:</strong> rate(下载速度), remaining(剩余队列), downloaded(已完成)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                        NZBget 认证设置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Control 用户名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.username', { required: 'NZBget Control 用户名是必填的' })}
                            className="input"
                            placeholder="NZBget ControlUsername"
                          />
                          {errors.widget?.username && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.username.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            NZBget 配置中的 ControlUsername
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Control 密码 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.password', { required: 'NZBget Control 密码是必填的' })}
                            type="text"
                            className="input"
                            placeholder="NZBget ControlPassword"
                          />
                          {errors.widget?.password && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.password.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            NZBget 配置中的 ControlPassword
                          </p>
                        </div>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示内容 <span className="text-xs text-gray-400">(最多3个字段)</span>
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showRate')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">下载速度</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showRemaining')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">剩余队列</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showDownloaded')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已完成</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://nzbget.host.or.ip'
                            const username = watch('widget.username') || 'controlusername'
                            const password = watch('widget.password') || 'controlpassword'

                            let config = `widget:
  type: nzbget
  url: ${url}
  username: ${username}
  password: ${password}`

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}


                {/* AdGuard Home 专用配置 */}
                {watchedWidgetType === 'adguard' && (
                  <div className="space-y-4">
                    {/* AdGuard Home 说明提示 */}
                    <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">🛡</span>
                        </div>
                        <div className="text-sm text-red-200">
                          <p className="font-medium mb-1">AdGuard Home DNS 防护小部件说明</p>
                          <p className="text-red-300 mb-2">
                            显示 AdGuard Home 的拦截统计、查询数量、过滤状态和延迟信息。
                          </p>
                          <div className="text-xs text-red-400 space-y-1">
                            <p>• <strong>URL:</strong> AdGuard Home 地址 (如: http://localhost:3000)</p>
                            <p>• <strong>用户名:</strong> 与 AdGuard Home Web 界面登录用户名相同</p>
                            <p>• <strong>密码:</strong> 与 AdGuard Home Web 界面登录密码相同</p>
                            <p>• <strong>显示内容:</strong> queries (查询数), blocked (拦截数), filtered (过滤率), latency (延迟)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                        AdGuard Home 配置
                      </h4>

                      {/* URL 配置 */}
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            AdGuard Home URL <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'AdGuard Home URL 是必填的' })}
                            className="input"
                            placeholder="http://localhost:3000"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            AdGuard Home 服务器地址，默认端口通常为 3000
                          </p>
                        </div>
                      </div>

                      {/* 认证配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.username', { required: 'AdGuard Home 用户名是必填的' })}
                            className="input"
                            placeholder="admin"
                          />
                          {errors.widget?.username && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.username.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Web 界面登录用户名
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            密码 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.password', { required: 'AdGuard Home 密码是必填的' })}
                            type="text"
                            className="input"
                            placeholder="password"
                          />
                          {errors.widget?.password && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.password.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Web 界面登录密码
                          </p>
                        </div>
                      </div>

                      {/* 显示内容选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示内容 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showQueries')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">总查询数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showBlocked')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">拦截数量</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showFiltered')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">过滤率</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showLatency')}
                              className="border-gray-600 text-red-600 focus:ring-red-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">延迟</span>
                          </label>
                        </div>
                      </div>

                      {/* 刷新间隔配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="5"
                          className="input"
                          placeholder="30"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          数据刷新间隔，建议 30-60 秒
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://adguard.host.or.ip'
                            const username = watch('widget.username') || 'admin'
                            const password = watch('widget.password') || 'password'
                            const showQueries = watch('widget.showQueries')
                            const showBlocked = watch('widget.showBlocked')
                            const showFiltered = watch('widget.showFiltered')
                            const showLatency = watch('widget.showLatency')
                            const refreshInterval = watch('widget.refreshInterval') || 30

                            const fields = []
                            if (showQueries) fields.push('queries')
                            if (showBlocked) fields.push('blocked')
                            if (showFiltered) fields.push('filtered')
                            if (showLatency) fields.push('latency')

                            let config = `widget:
  type: adguard
  url: ${url}
  username: ${username}
  password: ${password}`

                            if (refreshInterval !== 30) {
                              config += `
  refreshInterval: ${refreshInterval}000`
                            }

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Jackett 专用配置 */}
                {watchedWidgetType === 'jackett' && (
                  <div className="space-y-4">
                    {/* Jackett 说明提示 */}
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">🔍</span>
                        </div>
                        <div className="text-sm text-purple-200">
                          <p className="font-medium mb-1">Jackett 索引器管理小部件说明</p>
                          <p className="text-purple-300 mb-2">
                            显示 Jackett 的索引器配置状态和错误信息。
                          </p>
                          <div className="text-xs text-purple-400 space-y-1">
                            <p>• <strong>URL:</strong> Jackett 地址 (如: http://jackett.host.or.ip)</p>
                            <p>• <strong>密码:</strong> Jackett 管理员密码（如果设置了）</p>
                            <p>• <strong>显示字段:</strong> configured (已配置), errored (错误)</p>
                            <p>• <strong>官方文档:</strong> <a href="https://gethomepage.dev/widgets/services/jackett/" target="_blank" className="text-purple-300 hover:text-purple-200 underline">https://gethomepage.dev/widgets/services/jackett/</a></p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                        Jackett 配置
                      </h4>

                      {/* URL 配置 */}
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Jackett URL <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'Jackett URL 是必填的' })}
                            className="input"
                            placeholder="http://jackett.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Jackett 服务器地址
                          </p>
                        </div>
                      </div>

                      {/* 密码配置（可选） */}
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            管理员密码 <span className="text-xs text-gray-400">(可选)</span>
                          </label>
                          <input
                            {...register('widget.password')}
                            type="text"
                            className="input"
                            placeholder="password"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            如果 Jackett 设置了管理员密码，请在此填写
                          </p>
                        </div>
                      </div>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.configured')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已配置索引器</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.errored')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">错误索引器</span>
                          </label>
                        </div>
                      </div>

                      {/* 刷新间隔配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="5"
                          className="input"
                          placeholder="30"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          数据刷新间隔，建议 30-60 秒
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://jackett.host.or.ip'
                            const password = watch('widget.password')
                            const showConfigured = watch('widget.fields.configured')
                            const showErrored = watch('widget.fields.errored')
                            const refreshInterval = watch('widget.refreshInterval') || 30

                            const fields = []
                            if (showConfigured) fields.push('configured')
                            if (showErrored) fields.push('errored')

                            let config = `widget:
  type: jackett
  url: ${url}`

                            if (password) {
                              config += `
  password: ${password}`
                            }

                            if (refreshInterval !== 30) {
                              config += `
  refreshInterval: ${refreshInterval}000`
                            }

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mikrotik 专用配置 */}
                {watchedWidgetType === 'mikrotik' && (
                  <div className="space-y-4">
                    {/* Mikrotik 说明提示 */}
                    <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">🔧</span>
                        </div>
                        <div className="text-sm text-orange-200">
                          <p className="font-medium mb-1">Mikrotik 路由器监控小部件说明</p>
                          <p className="text-orange-300 mb-2">
                            监控 Mikrotik 路由器的系统状态，包括运行时间、CPU 负载、内存使用和 DHCP 租约数量。
                          </p>
                          <div className="text-xs text-orange-400 space-y-1">
                            <p>• <strong>URL:</strong> Mikrotik 路由器地址 (通常使用 HTTPS)</p>
                            <p>• <strong>认证:</strong> 路由器管理员用户名和密码</p>
                            <p>• <strong>支持字段:</strong> uptime, cpuLoad, memoryUsed, numberOfLeases</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                        Mikrotik 路由器配置
                      </h4>

                      {/* URL 配置 */}
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Mikrotik 路由器地址 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'Mikrotik 路由器地址是必填的' })}
                            className="input"
                            placeholder="https://192.168.1.1"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Mikrotik 路由器地址，建议使用 HTTPS 协议
                          </p>
                        </div>
                      </div>

                      {/* 认证配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.username', { required: '用户名是必填的' })}
                            className="input"
                            placeholder="admin"
                          />
                          {errors.widget?.username && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.username.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Mikrotik 路由器管理员用户名
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            密码 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.password', { required: '密码是必填的' })}
                            type="text"
                            className="input"
                            placeholder="password"
                          />
                          {errors.widget?.password && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.password.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Mikrotik 路由器管理员密码
                          </p>
                        </div>
                      </div>

                      {/* 监控字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          监控字段 <span className="text-xs text-gray-400">(最多4个)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.uptime')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">运行时间</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.cpuLoad')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">CPU 负载</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.memoryUsed')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">内存使用</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.numberOfLeases')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">DHCP 租约</span>
                          </label>
                        </div>
                        <p className="mt-2 text-xs text-gray-400">
                          选择要在小部件中显示的监控指标，最多可选择4个字段
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'https://192.168.1.1'
                            const username = watch('widget.username') || 'admin'
                            const password = watch('widget.password') || 'password'
                            const uptime = watch('widget.fields.uptime')
                            const cpuLoad = watch('widget.fields.cpuLoad')
                            const memoryUsed = watch('widget.fields.memoryUsed')
                            const numberOfLeases = watch('widget.fields.numberOfLeases')

                            const fields = []
                            if (uptime) fields.push('uptime')
                            if (cpuLoad) fields.push('cpuLoad')
                            if (memoryUsed) fields.push('memoryUsed')
                            if (numberOfLeases) fields.push('numberOfLeases')

                            let config = `widget:
  type: mikrotik
  url: ${url}
  username: ${username}
  password: ${password}`

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Home Assistant 配置 */}
                {watchedWidgetType === 'homeassistant' && (
                  <div className="space-y-4">
                    {/* Home Assistant 说明提示 */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">ℹ</span>
                        </div>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">Home Assistant 智能家居小部件说明</p>
                          <p className="text-blue-300 mb-2">
                            显示 Home Assistant 的实体状态和系统信息。
                          </p>
                          <div className="text-xs text-blue-400 space-y-1">
                            <p>• <strong>URL:</strong> Home Assistant 地址 (如: http://localhost:8123)</p>
                            <p>• <strong>Token:</strong> 长期访问令牌 (Long-lived access token)</p>
                            <p>• <strong>获取方法:</strong> 用户配置 → 安全 → 长期访问令牌</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Glances 专用配置 */}
                {watchedWidgetType === 'glances' && (
                  <div className="space-y-4">
                    {/* Glances 说明提示 */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">ℹ</span>
                        </div>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">Glances 监控小部件说明</p>
                          <p className="text-blue-300">
                            Glances 是系统监控小部件，服务名称和访问地址为可选项。
                            如果不填写服务名称，将根据选择的监控指标自动生成名称。
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        Glances 监控配置
                      </h4>

                      {/* URL 配置 */}
                      <div className="grid grid-cols-1 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Glances 服务器地址 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'Glances 服务器地址是必填的' })}
                            className="input"
                            placeholder="http://localhost:61208"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Glances 服务器地址，默认端口通常为 61208
                          </p>
                        </div>
                      </div>

                      {/* 显示指标选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示指标 <span className="text-xs text-gray-400">(单选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              name="widget.metric"
                              value="info"
                              checked={watch('widget.selectedMetric') === 'info'}
                              onClick={() => {
                                const currentValue = watch('widget.selectedMetric')
                                if (currentValue === 'info') {
                                  setValue('widget.selectedMetric', '')
                                } else {
                                  setValue('widget.selectedMetric', 'info')
                                }
                              }}
                              disabled={customFields.length > 0}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">系统信息</span>
                          </label>
                          <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              name="widget.metric"
                              value="cpu"
                              checked={watch('widget.selectedMetric') === 'cpu'}
                              onClick={() => {
                                const currentValue = watch('widget.selectedMetric')
                                if (currentValue === 'cpu') {
                                  setValue('widget.selectedMetric', '')
                                } else {
                                  setValue('widget.selectedMetric', 'cpu')
                                }
                              }}
                              disabled={customFields.length > 0}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">CPU 使用率</span>
                          </label>
                          <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              name="widget.metric"
                              value="memory"
                              checked={watch('widget.selectedMetric') === 'memory'}
                              onClick={() => {
                                const currentValue = watch('widget.selectedMetric')
                                if (currentValue === 'memory') {
                                  setValue('widget.selectedMetric', '')
                                } else {
                                  setValue('widget.selectedMetric', 'memory')
                                }
                              }}
                              disabled={customFields.length > 0}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">内存使用</span>
                          </label>
                          <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              name="widget.metric"
                              value="process"
                              checked={watch('widget.selectedMetric') === 'process'}
                              onClick={() => {
                                const currentValue = watch('widget.selectedMetric')
                                if (currentValue === 'process') {
                                  setValue('widget.selectedMetric', '')
                                } else {
                                  setValue('widget.selectedMetric', 'process')
                                }
                              }}
                              disabled={customFields.length > 0}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">进程监控</span>
                          </label>
                          <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              name="widget.metric"
                              value="containers"
                              checked={watch('widget.selectedMetric') === 'containers'}
                              onClick={() => {
                                const currentValue = watch('widget.selectedMetric')
                                if (currentValue === 'containers') {
                                  setValue('widget.selectedMetric', '')
                                } else {
                                  setValue('widget.selectedMetric', 'containers')
                                }
                              }}
                              disabled={customFields.length > 0}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">容器监控</span>
                          </label>
                          <label className={`flex items-center space-x-2 cursor-pointer ${customFields.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              name="widget.metric"
                              value="network"
                              checked={watch('widget.selectedMetric') === 'network'}
                              onClick={() => {
                                const currentValue = watch('widget.selectedMetric')
                                if (currentValue === 'network') {
                                  setValue('widget.selectedMetric', '')
                                } else {
                                  setValue('widget.selectedMetric', 'network')
                                }
                              }}
                              disabled={customFields.length > 0}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">网络流量</span>
                          </label>
                        </div>
                        {customFields.length > 0 && (
                          <p className="text-xs text-yellow-400 mt-2">
                            💡 使用自定义字段时，预设指标将被禁用
                          </p>
                        )}
                      </div>

                      {/* 自定义监控字段 */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <label className="block text-sm font-medium text-gray-300">
                              自定义监控字段
                            </label>
                            <button
                              type="button"
                              onClick={() => setShowCustomFieldsHelp(!showCustomFieldsHelp)}
                              className="w-5 h-5 bg-blue-600 text-white text-xs rounded-full hover:bg-blue-700 transition-colors flex items-center justify-center"
                              title="查看帮助文档"
                            >
                              ?
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setCustomFields([...customFields, { field: '', label: '' }])
                              // 清除预设指标选择
                              setValue('widget.selectedMetric', '')
                            }}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                          >
                            + 添加字段
                          </button>
                        </div>

                        {customFields.length > 0 && (
                          <div className="space-y-3">
                            {customFields.map((customField, index) => (
                              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-900 rounded border border-gray-600">
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    placeholder="字段名 (如: sensors)"
                                    value={customField.field}
                                    onChange={(e) => {
                                      const newFields = [...customFields]
                                      newFields[index].field = e.target.value
                                      setCustomFields(newFields)
                                    }}
                                    className="input text-sm"
                                  />
                                </div>
                                <div className="flex-1">
                                  <input
                                    type="text"
                                    placeholder="显示名称 (如: 传感器)"
                                    value={customField.label}
                                    onChange={(e) => {
                                      const newFields = [...customFields]
                                      newFields[index].label = e.target.value
                                      setCustomFields(newFields)
                                    }}
                                    className="input text-sm"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newFields = customFields.filter((_, i) => i !== index)
                                    setCustomFields(newFields)
                                    // 如果删除后没有自定义字段了，恢复预设指标可用状态
                                    if (newFields.length === 0) {
                                      // 可以选择恢复之前的选择或保持清空状态
                                      // setValue('widget.selectedMetric', '')
                                    }
                                  }}
                                  className="px-2 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                                >
                                  删除
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {customFields.length === 0 && (
                          <div className="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-600 rounded">
                            点击"添加字段"来添加自定义监控指标
                            <br />
                            <span className="text-xs text-gray-500">
                              例如：sensors (传感器)、processes (进程)、docker (容器) 等
                            </span>
                          </div>
                        )}

                        {/* 帮助文档 */}
                        {showCustomFieldsHelp && (
                          <div className="mt-4 p-4 bg-blue-900/20 border border-blue-600/30 rounded-lg">
                            <h4 className="text-sm font-semibold text-blue-300 mb-3">📖 自定义监控字段帮助文档</h4>

                            <div className="space-y-4 text-sm text-gray-300">
                              <div>
                                <h5 className="font-medium text-blue-200 mb-2">🌡️ 温度传感器 (sensor:)</h5>
                                <div className="space-y-1 text-xs">
                                  <div><code className="bg-gray-800 px-1 rounded">sensor:Package id 0</code> - CPU 整体温度</div>
                                  <div><code className="bg-gray-800 px-1 rounded">sensor:Core 0</code> - CPU 核心0温度</div>
                                  <div><code className="bg-gray-800 px-1 rounded">sensor:Core 1</code> - CPU 核心1温度</div>
                                  <div><code className="bg-gray-800 px-1 rounded">sensor:it8613 0</code> - 主板传感器温度</div>
                                </div>
                              </div>

                              <div>
                                <h5 className="font-medium text-blue-200 mb-2">💾 磁盘监控 (disk: / fs:)</h5>
                                <div className="space-y-1 text-xs">
                                  <div><code className="bg-gray-800 px-1 rounded">disk:sda</code> - 磁盘 sda 的 I/O 数据</div>
                                  <div><code className="bg-gray-800 px-1 rounded">fs:/</code> - 根分区磁盘使用情况</div>
                                  <div><code className="bg-gray-800 px-1 rounded">fs:/home</code> - /home 分区磁盘使用情况</div>
                                </div>
                              </div>

                              <div>
                                <h5 className="font-medium text-blue-200 mb-2">🌐 网络监控 (network:)</h5>
                                <div className="space-y-1 text-xs">
                                  <div><code className="bg-gray-800 px-1 rounded">network:eth0</code> - 以太网接口流量</div>
                                  <div><code className="bg-gray-800 px-1 rounded">network:wlan0</code> - 无线网络接口流量</div>
                                  <div><code className="bg-gray-800 px-1 rounded">network:enp0s25</code> - 特定网络接口流量</div>
                                </div>
                              </div>

                              <div>
                                <h5 className="font-medium text-blue-200 mb-2">🎮 GPU 监控 (gpu:)</h5>
                                <div className="space-y-1 text-xs">
                                  <div><code className="bg-gray-800 px-1 rounded">gpu:0</code> - 第一个 GPU 使用情况</div>
                                  <div><code className="bg-gray-800 px-1 rounded">gpu:1</code> - 第二个 GPU 使用情况</div>
                                </div>
                              </div>

                              <div>
                                <h5 className="font-medium text-blue-200 mb-2">🔧 其他监控字段</h5>
                                <div className="space-y-1 text-xs">
                                  <div><code className="bg-gray-800 px-1 rounded">containers</code> - Docker 容器列表</div>
                                  <div><code className="bg-gray-800 px-1 rounded">process</code> - 前5个进程 (按CPU使用率)</div>
                                  <div><code className="bg-gray-800 px-1 rounded">info</code> - 系统信息概览</div>
                                </div>
                              </div>

                              <div className="pt-2 border-t border-blue-600/30">
                                <h5 className="font-medium text-yellow-300 mb-2">💡 使用提示</h5>
                                <ul className="space-y-1 text-xs text-gray-400">
                                  <li>• <strong>字段名</strong>：填写 Glances API 中的确切字段名</li>
                                  <li>• <strong>显示名称</strong>：填写用户友好的中文名称</li>
                                  <li>• 可访问 <code className="bg-gray-800 px-1 rounded">http://your-glances-url/api/4/sensors</code> 查看可用传感器</li>
                                  <li>• Homepage 只显示第一个选中的监控指标</li>
                                  <li>• 如需显示多个指标，请创建多个服务</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* 高级配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            磁盘分区
                          </label>
                          <input
                            {...register('widget.disk')}
                            className="input"
                            placeholder="/ (默认根分区)"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            指定要监控的磁盘分区，如 /home, /var 等
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            网络接口
                          </label>
                          <input
                            {...register('widget.interface')}
                            className="input"
                            placeholder="eth0 (默认主网卡)"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            指定要监控的网络接口，如 eth0, ens33, wlan0 等
                          </p>
                        </div>
                      </div>

                      {/* 认证配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名
                          </label>
                          <input
                            {...register('widget.username')}
                            className="input"
                            placeholder="可选，如果启用了认证"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            如果 Glances 启用了认证，请输入用户名
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            密码
                          </label>
                          <input
                            {...register('widget.password')}
                            type="text"
                            className="input"
                            placeholder="可选，如果启用了认证"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            如果 Glances 启用了认证，请输入密码
                          </p>
                        </div>
                      </div>

                      {/* API 版本和刷新间隔配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Glances API 版本
                          </label>
                          <select
                            {...register('widget.version', { valueAsNumber: true })}
                            className="input"
                            defaultValue={4}
                          >
                            <option value={3}>版本 3 (旧版本)</option>
                            <option value={4}>版本 4 (推荐)</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-400">
                            选择 Glances 服务的 API 版本，大多数新版本使用 v4
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            刷新间隔 (秒)
                          </label>
                          <input
                            {...register('widget.refreshInterval', { valueAsNumber: true })}
                            type="number"
                            min="1"
                            className="input"
                            placeholder="5"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            数据刷新间隔，建议 5-30 秒
                          </p>
                        </div>
                      </div>

                      {/* 高级配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            磁盘单位
                          </label>
                          <select
                            {...register('widget.diskUnits')}
                            className="input"
                          >
                            <option value="bytes">字节 (默认)</option>
                            <option value="bbytes">二进制字节</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-400">
                            磁盘数据的显示单位，仅适用于磁盘监控
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            数据点限制
                          </label>
                          <input
                            {...register('widget.pointsLimit', { valueAsNumber: true })}
                            type="number"
                            min="5"
                            max="50"
                            className="input"
                            placeholder="15"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            图表中显示的数据点数量，默认15个
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            图表显示
                          </label>
                          <select
                            {...register('widget.chart')}
                            className="input"
                          >
                            <option value="true">显示图表 (默认)</option>
                            <option value="false">紧凑模式</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-400">
                            选择显示完整图表或紧凑模式
                          </p>
                        </div>
                      </div>



                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://localhost:61208'
                            const selectedMetric = watch('widget.selectedMetric')
                            const username = watch('widget.username')
                            const password = watch('widget.password')
                            const version = watch('widget.version') || 4
                            const refreshInterval = watch('widget.refreshInterval')
                            const diskUnits = watch('widget.diskUnits')
                            const pointsLimit = watch('widget.pointsLimit')
                            const chart = watch('widget.chart')

                            // 添加自定义字段到预览
                            const customFieldsList = customFields.filter(cf => cf.field && cf.field.trim()).map(cf => cf.field.trim())

                            let config = `widget:
  type: glances
  url: ${url}`

                            // 认证信息
                            if (username) {
                              config += `
  username: ${username}`
                            }
                            if (password) {
                              config += `
  password: ${password}`
                            }

                            // API 版本
                            if (version !== 4) {
                              config += `
  version: ${version}`
                            }

                            // 监控指标
                            if (selectedMetric || customFieldsList.length > 0) {
                              let metric = selectedMetric || customFieldsList[0]

                              // 根据指标类型构建正确的 metric 字符串
                              const networkInterface = watch('widget.interface')
                              const disk = watch('widget.disk')

                              if (metric === 'network' && networkInterface) {
                                metric = `network:${networkInterface}`
                              } else if (metric === 'disk' && disk) {
                                metric = `fs:${disk}`
                              }

                              config += `
  metric: ${metric}`
                            }

                            // 磁盘单位
                            if (diskUnits && diskUnits !== 'bytes') {
                              config += `
  diskUnits: ${diskUnits}`
                            }

                            // 刷新间隔
                            if (refreshInterval) {
                              config += `
  refreshInterval: ${refreshInterval * 1000}`
                            }

                            // 数据点限制
                            if (pointsLimit && pointsLimit !== 15) {
                              config += `
  pointsLimit: ${pointsLimit}`
                            }

                            // 图表显示
                            if (chart === 'false') {
                              config += `
  chart: false`
                            }

                            // 如果选择了多个指标，显示提示
                            if (customFieldsList.length > 1) {
                              config += `

# 注意: Homepage Glances 小部件只支持单个指标
# 当前显示: ${customFieldsList[0]}
# 其他自定义字段: ${customFieldsList.slice(1).join(', ')}
# 如需显示多个指标，请创建多个服务`
                            } else if (customFieldsList.length > 0 && selectedMetric) {
                              config += `

# 注意: 同时选择了预设指标和自定义字段，将使用预设指标
# 自定义字段: ${customFieldsList.join(', ')}`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Synology Disk Station 专用配置 */}
                {watchedWidgetType === 'diskstation' && (
                  <div className="space-y-4">
                    {/* Synology Disk Station 说明提示 */}
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">ℹ</span>
                        </div>
                        <div className="text-sm text-purple-200">
                          <p className="font-medium mb-1">Synology Disk Station 监控小部件说明</p>
                          <p className="text-purple-300">
                            监控 Synology NAS 的系统状态，包括运行时间、存储空间、CPU 和内存使用情况。
                            需要管理员权限账户，不支持 2FA 认证。
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                        Synology Disk Station 配置
                      </h4>

                      {/* DSM URL 配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          DSM URL <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register('widget.url', { required: 'DSM URL 是必填的' })}
                          className="input"
                          placeholder="http://diskstation.host.or.ip:port"
                        />
                        {errors.widget?.url && (
                          <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">
                          Synology DSM 的访问地址，包含端口号
                        </p>
                      </div>

                      {/* 用户名和密码配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.username', { required: '用户名是必填的' })}
                            className="input"
                            placeholder="admin"
                          />
                          {errors.widget?.username && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.username.message}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            密码 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.password', { required: '密码是必填的' })}
                            type="text"
                            className="input"
                            placeholder="password"
                          />
                          {errors.widget?.password && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.password.message}</p>
                          )}
                        </div>
                      </div>

                      {/* 存储卷配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          存储卷 <span className="text-xs text-gray-400">(可选)</span>
                        </label>
                        <input
                          {...register('widget.volume')}
                          className="input"
                          placeholder="volume_1, volume_2, volume_3..."
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          指定要显示存储空间的卷，格式为 volume_N（如 volume_1, volume_2）。
                          如果省略，将显示第一个返回的卷的存储空间。
                        </p>
                      </div>

                      {/* 监控字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          监控字段 <span className="text-red-400">*</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.uptime')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">运行时间</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.volumeAvailable')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">存储空间</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.cpu')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">CPU 使用率</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.memory')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">内存使用</span>
                          </label>
                        </div>
                        <p className="mt-1 text-xs text-gray-400">
                          选择要显示的监控指标，至少需要选择一个
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://diskstation.host.or.ip:port'
                            const username = watch('widget.username') || 'username'
                            const password = watch('widget.password') ? '***' : 'password'
                            const volume = watch('widget.volume')

                            const fields = []
                            if (watch('widget.fields.uptime')) fields.push('uptime')
                            if (watch('widget.fields.volumeAvailable')) fields.push('volumeAvailable')
                            if (watch('widget.fields.cpu')) fields.push('resources.cpu')
                            if (watch('widget.fields.memory')) fields.push('resources.mem')

                            let config = `widget:
  type: diskstation
  url: ${url}
  username: ${username}
  password: ${password}`

                            if (volume && volume.trim()) {
                              config += `
  volume: ${volume.trim()}`
                            }

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bilibili 专用配置 */}
                {watchedWidgetType === 'bilibili' && (
                  <div className="space-y-4">
                    {/* Bilibili 说明提示 */}
                    <div className="bg-pink-900/20 border border-pink-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">B</span>
                        </div>
                        <div className="text-sm text-pink-200">
                          <p className="font-medium mb-1">Bilibili 视频数据监控说明</p>
                          <p className="text-pink-300">
                            监控指定 Bilibili 视频的观看数、点赞数、收藏数、投币数等数据。
                            请输入视频的 BV 号或 AV 号。
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
                        Bilibili 视频配置
                      </h4>

                      {/* 基础配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            视频 ID <span className="text-red-400">*</span>
                          </label>
                          <div className="flex space-x-2">
                            <input
                              {...register('widget.videoId', {
                                required: 'Bilibili 视频 ID 是必填的'
                              })}
                              className="input flex-1"
                              placeholder="BV1JFvNzHEDQ 或 av170001"
                            />
                            <button
                              type="button"
                              onClick={() => fetchVideoInfo(watchedVideoId)}
                              disabled={videoInfoLoading || !watchedVideoId}
                              className="px-3 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                            >
                              {videoInfoLoading ? '获取中...' : '获取信息'}
                            </button>
                          </div>
                          {errors.widget?.videoId && (
                            <p className="mt-1 text-sm text-red-400">{errors.widget.videoId.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            支持 BV 号（如：BV1JFvNzHEDQ）或 AV 号（如：av170001）
                          </p>
                          {videoInfo && (
                            <div className="mt-2 p-2 bg-green-900/20 border border-green-500/30 rounded text-sm">
                              <p className="text-green-300">✅ 视频：{videoInfo.title}</p>
                              <p className="text-green-400 text-xs">作者：{videoInfo.author} | 观看：{videoInfo.viewFormatted}</p>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            API 基础地址
                          </label>
                          <input
                            {...register('widget.apiBaseUrl')}
                            className="input"
                            placeholder="http://配置管理器地址[:端口]"
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            配置管理器的访问地址，留空将自动检测
                          </p>
                        </div>
                      </div>

                      {/* 显示数据选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示数据 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showView')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">观看数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showLike')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">点赞数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showFavorite')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">收藏数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showCoin')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">投币数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showShare')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">分享数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showReply')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">评论数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showDanmaku')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">弹幕数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showDislike')}
                              className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">点踩数</span>
                          </label>
                        </div>
                      </div>

                      {/* 未读消息（需要扫码登录） */}
                      <div className="mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer" style={{ display: 'none' }}>
                          <input
                            type="checkbox"
                            {...register('widget.enableUnread')}
                            className="border-gray-600 text-pink-600 focus:ring-pink-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">{biliLoginStatus === 'success' ? '显示未读消息（已登录状态）' : '显示未读消息（需要扫码登录）'}</span>
                        </label>

                        {watch('widget.enableUnread') && (
                          <div className="mt-3 p-3 bg-pink-950/40 rounded border border-pink-600/20 space-y-3" style={{ display: 'none' }}>
                            <p className="text-xs text-pink-200">
                              启用后需要先完成一次 Bilibili 扫码登录以授权获取未读消息。登录状态保存在服务器端，避免暴露敏感信息。
                            </p>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className={`px-3 py-2 rounded-md text-sm ${biliLoginStatus === 'success' ? 'bg-green-600 text-white cursor-default' : 'bg-pink-600 hover:bg-pink-700 text-white'}`}
                                onClick={biliLoginStatus === 'success' ? undefined : startBiliLogin}
                                disabled={biliLoginStatus === 'success'}
                              >
                                {biliLoginStatus === 'success' ? '已登录' : '立即扫码登录'}
                              </button>
                              <button
                                type="button"
                                className="px-3 py-2 bg-gray-700 text-gray-100 rounded-md hover:bg-gray-600 text-sm"
                                onClick={logoutBilibili}
                              >
                                退出登录
                              </button>
                            </div>

                            <p className="text-xs text-gray-400">
                              完成登录后，可在下方映射里选择“未读消息数”字段以显示在卡片中。
                            </p>
                          </div>
                        )}
                      </div>

                      {/* 刷新间隔配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (分钟)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="1"
                          className="input"
                          placeholder="30"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                          数据刷新间隔，建议 30-60 分钟（避免频繁请求）
                        </p>
                      </div>

                      {/* 显示布局方式配置 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示布局方式
                        </label>
                        <select
                          {...register('widget.displayMode')}
                          className="input"
                        >
                          <option value="block">块状布局 (最多显示4个字段)</option>
                          <option value="list">列表布局 (可显示所有字段)</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-400">
                          块状布局：紧凑显示，最多4个字段；列表布局：可显示所有选中的字段
                        </p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const videoId = watch('widget.videoId') || 'BV1JFvNzHEDQ'
                            const showView = watch('widget.showView')
                            const showLike = watch('widget.showLike')
                            const showFavorite = watch('widget.showFavorite')
                            const showCoin = watch('widget.showCoin')
                            const showShare = watch('widget.showShare')
                            const showReply = watch('widget.showReply')
                            const showDanmaku = watch('widget.showDanmaku')
                            const showDislike = watch('widget.showDislike')
                            const refreshInterval = watch('widget.refreshInterval') || 30
                            const displayMode = watch('widget.displayMode') || 'block'

                            const fields = []
                            if (showView) fields.push('view')
                            if (showLike) fields.push('like')
                            if (showFavorite) fields.push('favorite')
                            if (showCoin) fields.push('coin')
                            if (showShare) fields.push('share')
                            if (showReply) fields.push('reply')
                            if (showDanmaku) fields.push('danmaku')
                            if (showDislike) fields.push('dislike')

                            let config = `widget:
  type: bilibili
  videoId: ${videoId}
  refreshInterval: ${refreshInterval}`

                            if (displayMode !== 'block') {
                              config += `
  displayMode: ${displayMode}`
                            }

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.join(', ')}]`
                            }

                            // 添加字段数量提示
                            if (fields.length > 4 && displayMode === 'block') {
                              config += `
  # 注意：块状布局最多显示4个字段，当前选择了${fields.length}个字段
  # 建议使用列表布局以显示所有字段`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bilibili 扫码登录弹窗 */}
                {showBiliLogin && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/60" onClick={stopBiliLogin} />
                    <div className="relative bg-gray-800 border border-gray-700 rounded-xl p-5 w-full max-w-md z-10" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-gray-100 font-semibold">Bilibili 扫码登录</h4>
                        <button type="button" onClick={stopBiliLogin} className="text-gray-300 hover:text-white">✕</button>
                      </div>
                      <div className="space-y-3">
                        {biliQrUrl ? (
                          <div className="flex flex-col items-center">
                            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(biliQrUrl)}`} alt="QR" className="rounded bg-white p-2" />
                            <p className="text-xs text-gray-400 mt-2">请使用 Bilibili APP 扫码并确认</p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-300">正在创建二维码...</p>
                        )}

                        <div className="p-3 bg-gray-900/60 rounded border border-gray-700">
                          <p className="text-xs text-gray-400 mb-2">或粘贴已获取的 Bilibili Cookie</p>
                          <textarea
                            value={biliCookieInput}
                            onChange={(e) => setBiliCookieInput(e.target.value)}
                            className="w-full bg-gray-900 border border-gray-700 rounded p-2 text-xs text-gray-200"
                            rows={3}
                            placeholder="SESSDATA=...; bili_jct=...; DedeUserID=..."
                          />
                          <div className="mt-2 flex justify-end">
                            <button type="button" onClick={(e) => { e.stopPropagation(); submitBiliCookie() }} className="px-3 py-1.5 bg-pink-600 text-white rounded hover:bg-pink-700 text-sm">保存 Cookie</button>
                          </div>
                        </div>

                        <div className="text-xs text-gray-500">
                          状态：{biliLoginStatus}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Custom API 专用配置 */}
                {watchedWidgetType === 'customapi' && (
                  <div className="space-y-4">
                    {/* Custom API 说明提示 */}
                    <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">API</span>
                        </div>
                        <div className="text-sm text-indigo-200">
                          <p className="font-medium mb-1">自定义 API 小部件说明</p>
                          <p className="text-xs text-indigo-300 mb-2">
                            支持从自定义API获取数据并显示。可以配置请求方法、头部、请求体、数据映射和显示格式。
                          </p>
                          <div className="text-xs text-indigo-300 space-y-1">
                            <p>• 支持 GET/POST 请求方法</p>
                            <p>• 支持自定义请求头和请求体</p>
                            <p>• 支持多种数据格式：text、number、float、percent、duration、bytes、bitrate、size、date、relativeDate</p>
                            <p>• 支持数据转换：remap、scale、prefix、suffix</p>
                            <p>• 支持多种显示模式：block、list、dynamic-list</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* API 基础配置 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          API URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('widget.url', { required: 'API URL 是必填项' })}
                          className="input"
                          placeholder="http://api.example.com/data"
                        />
                        {errors.widget?.url && (
                          <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', {
                            valueAsNumber: true,
                            min: { value: 5, message: '最小刷新间隔为5秒' }
                          })}
                          type="number"
                          className="input"
                          placeholder="10"
                          min="5"
                        />
                        {errors.widget?.refreshInterval && (
                          <p className="text-red-500 text-sm mt-1">{errors.widget.refreshInterval.message}</p>
                        )}
                      </div>
                    </div>

                    {/* 认证配置 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          用户名
                        </label>
                        <input
                          {...register('widget.username')}
                          className="input"
                          placeholder="API用户名"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          密码
                        </label>
                        <input
                          {...register('widget.password')}
                          type="text"
                          className="input"
                          placeholder="API密码"
                        />
                      </div>
                    </div>

                    {/* 请求配置 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          请求方法
                        </label>
                        <select
                          {...register('widget.method')}
                          className="select"
                        >
                          <option value="GET">GET</option>
                          <option value="POST">POST</option>
                          <option value="PUT">PUT</option>
                          <option value="DELETE">DELETE</option>
                          <option value="PATCH">PATCH</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示模式
                        </label>
                        <select
                          {...register('widget.display')}
                          className="select"
                        >
                          <option value="block">块状显示 (默认)</option>
                          <option value="list">列表显示</option>
                          <option value="dynamic-list">动态列表</option>
                        </select>
                      </div>
                    </div>

                    {/* 自定义请求头 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        自定义请求头 (JSON格式)
                      </label>
                      <textarea
                        {...register('widget.headers')}
                        className="input min-h-[80px]"
                        placeholder='{"X-API-Token": "your-token", "Content-Type": "application/json"}'
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        可选，JSON格式的请求头对象
                      </p>
                    </div>

                    {/* 请求体 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        请求体 (JSON格式)
                      </label>
                      <textarea
                        {...register('widget.requestBody')}
                        className="input min-h-[80px]"
                        placeholder='{"query": "data", "filter": "active"}'
                      />
                      <p className="text-xs text-gray-400 mt-1">
                        可选，POST/PUT/PATCH请求的请求体，支持JSON对象或字符串
                      </p>
                    </div>

                    {/* 数据映射配置 */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-300">
                          数据映射配置 <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={addMapping}
                          className="btn-secondary btn-sm"
                        >
                          <PlusIcon className="w-4 h-4 mr-1" />
                          添加映射
                        </button>
                      </div>

                      {widgetMappings.map((mapping, index) => (
                        <div key={index} className="bg-gray-800 rounded-lg p-4 mb-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                字段路径
                              </label>
                              <input
                                value={mapping.field || ''}
                                onChange={(e) => updateMapping(index, 'field', e.target.value)}
                                className="input"
                                placeholder="data.field 或 path.to.nested.field"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                显示标签
                              </label>
                              <input
                                value={mapping.label || ''}
                                onChange={(e) => updateMapping(index, 'label', e.target.value)}
                                className="input"
                                placeholder="字段显示名称"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                数据格式
                              </label>
                              <select
                                value={mapping.format || 'text'}
                                onChange={(e) => updateMapping(index, 'format', e.target.value)}
                                className="select"
                              >
                                <option value="text">文本</option>
                                <option value="number">数字</option>
                                <option value="float">浮点数</option>
                                <option value="percent">百分比</option>
                                <option value="duration">持续时间</option>
                                <option value="bytes">字节</option>
                                <option value="bitrate">比特率</option>
                                <option value="size">大小</option>
                                <option value="date">日期</option>
                                <option value="relativeDate">相对日期</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                语言环境
                              </label>
                              <input
                                value={mapping.locale || ''}
                                onChange={(e) => updateMapping(index, 'locale', e.target.value)}
                                className="input"
                                placeholder="zh-CN, en-US, nl"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                日期样式
                              </label>
                              <select
                                value={mapping.dateStyle || 'long'}
                                onChange={(e) => updateMapping(index, 'dateStyle', e.target.value)}
                                className="select"
                              >
                                <option value="full">完整</option>
                                <option value="long">长格式</option>
                                <option value="medium">中等</option>
                                <option value="short">短格式</option>
                              </select>
                            </div>
                          </div>

                          {/* 数据转换配置 */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                数值缩放
                              </label>
                              <input
                                value={mapping.scale || ''}
                                onChange={(e) => updateMapping(index, 'scale', e.target.value)}
                                className="input"
                                placeholder="0.001, 1/16"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                前缀
                              </label>
                              <input
                                value={mapping.prefix || ''}
                                onChange={(e) => updateMapping(index, 'prefix', e.target.value)}
                                className="input"
                                placeholder="$"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                后缀
                              </label>
                              <input
                                value={mapping.suffix || ''}
                                onChange={(e) => updateMapping(index, 'suffix', e.target.value)}
                                className="input"
                                placeholder="kW, MB/s"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-1">
                                时间样式
                              </label>
                              <select
                                value={mapping.timeStyle || 'medium'}
                                onChange={(e) => updateMapping(index, 'timeStyle', e.target.value)}
                                className="select"
                              >
                                <option value="full">完整</option>
                                <option value="long">长格式</option>
                                <option value="medium">中等</option>
                                <option value="short">短格式</option>
                              </select>
                            </div>
                          </div>

                          {/* 值重映射配置 */}
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                              值重映射 (JSON格式)
                            </label>
                            <textarea
                              value={mapping.remap ? JSON.stringify(mapping.remap, null, 2) : ''}
                              onChange={(e) => {
                                try {
                                  const remap = e.target.value ? JSON.parse(e.target.value) : null;
                                  updateMapping(index, 'remap', remap);
                                } catch (err) {
                                  // 保持原值，等待用户完成输入
                                }
                              }}
                              className="input min-h-[60px] font-mono text-sm"
                              placeholder='[{"value": 0, "to": "None"}, {"value": 1, "to": "Connected"}, {"any": true, "to": "Unknown"}]'
                            />
                            <p className="text-xs text-gray-400 mt-1">
                              可选，用于将API返回值映射为更友好的显示文本
                            </p>
                          </div>

                          {/* 删除按钮 */}
                          <div className="flex justify-end mt-3">
                            <button
                              type="button"
                              onClick={() => removeMapping(index)}
                              className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 rounded-lg"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}

                      {widgetMappings.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                          <p>暂无数据映射配置</p>
                          <p className="text-sm">点击"添加映射"按钮开始配置</p>
                        </div>
                      )}
                    </div>

                    {/* 配置预览 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        配置预览
                      </label>
                      <div className="bg-gray-900 rounded-lg p-4">
                        <pre className="text-sm text-gray-300 overflow-x-auto">
                          {(() => {
                            const formData = getValues();
                            const config = {
                              widget: {
                                type: 'customapi',
                                url: formData.widget?.url || '',
                                ...(formData.widget?.refreshInterval && { refreshInterval: formData.widget.refreshInterval * 1000 }),
                                ...(formData.widget?.username && { username: formData.widget.username }),
                                ...(formData.widget?.password && { password: formData.widget.password }),
                                ...(formData.widget?.method && formData.widget.method !== 'GET' && { method: formData.widget.method }),
                                ...(formData.widget?.display && formData.widget.display !== 'block' && { display: formData.widget.display }),
                                ...(formData.widget?.headers && { headers: JSON.parse(formData.widget.headers || '{}') }),
                                ...(formData.widget?.requestBody && { requestBody: JSON.parse(formData.widget.requestBody || '{}') }),
                                mappings: widgetMappings.filter(m => m.field && m.label).map(mapping => ({
                                  field: mapping.field,
                                  label: mapping.label,
                                  ...(mapping.format && mapping.format !== 'text' && { format: mapping.format }),
                                  ...(mapping.locale && { locale: mapping.locale }),
                                  ...(mapping.dateStyle && mapping.dateStyle !== 'long' && { dateStyle: mapping.dateStyle }),
                                  ...(mapping.timeStyle && mapping.timeStyle !== 'medium' && { timeStyle: mapping.timeStyle }),
                                  ...(mapping.scale && { scale: mapping.scale }),
                                  ...(mapping.prefix && { prefix: mapping.prefix }),
                                  ...(mapping.suffix && { suffix: mapping.suffix }),
                                  ...(mapping.remap && { remap: mapping.remap })
                                }))
                              }
                            };
                            return JSON.stringify(config, null, 2);
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* 项目统计专用配置 */}
                {watchedWidgetType === 'stats' && (
                  <div className="space-y-4">
                    {/* 项目统计说明提示 */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">📊</span>
                        </div>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">项目统计小部件说明</p>
                          <p className="text-xs text-blue-300 mb-2">
                            显示Homepage Config Manager项目的统计信息，包括服务总数、小部件数量、书签数量、分组数量、备份数量等。
                          </p>
                          <div className="text-xs text-blue-300 space-y-1">
                            <p>• <strong>本地统计:</strong> 显示当前配置的统计信息</p>
                            <p>• <strong>Homepage统计:</strong> 可选，连接Homepage实例获取实际运行统计</p>
                            <p>• <strong>对比功能:</strong> 验证配置同步情况</p>
                            <p>• <strong>密码保护:</strong> 支持Homepage密码保护</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 统计配置 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Homepage地址 (可选)
                        </label>
                        <input
                          {...register('widget.homepageUrl')}
                          className="input"
                          placeholder="http://localhost:3000"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          可选，用于获取Homepage实例的统计信息
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          访问密码 (可选)
                        </label>
                        <input
                          {...register('widget.homepagePassword')}
                          type="text"
                          className="input"
                          placeholder="Homepage访问密码"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          可选，如果Homepage设置了密码保护
                        </p>
                      </div>
                    </div>

                    {/* 显示选项 */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        显示选项
                      </label>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showServices')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">服务总数</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showWidgets')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">小部件数量</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showBookmarks')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">书签数量</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showGroups')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">分组总数</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showBackups')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">备份数量</span>
                        </label>

                      </div>
                    </div>

                    {/* 刷新配置 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="30"
                          className="input"
                          placeholder="60"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          建议30-3600秒，避免频繁请求
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示模式
                        </label>
                        <select
                          {...register('widget.display')}
                          className="select"
                        >
                          <option value="block">块状显示</option>
                          <option value="list">列表显示</option>
                          <option value="grid">网格显示</option>
                        </select>
                      </div>
                    </div>

                    {/* 配置预览 */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        配置预览
                      </label>
                      <div className="bg-gray-900 rounded-lg p-4">
                        <pre className="text-sm text-gray-300 overflow-x-auto">
                          {(() => {
                            const formData = getValues();
                            const config = {
                              widget: {
                                type: 'customapi',
                                url: `${window.location.protocol}//${window.location.host}/api/stats`,
                                refreshInterval: (formData.widget?.refreshInterval || 60) * 1000,
                                method: 'GET',
                                display: formData.widget?.display || 'block',
                                ...(formData.widget?.homepageUrl && {
                                  headers: {
                                    'X-Homepage-URL': formData.widget.homepageUrl,
                                    ...(formData.widget?.homepagePassword && { 'X-Homepage-Password': formData.widget.homepagePassword })
                                  }
                                }),
                                mappings: [
                                  ...(formData.widget?.showServices === true ? [{
                                    field: 'data.local.services',
                                    label: '服务',
                                    format: 'number'
                                  }] : []),
                                  ...(formData.widget?.showWidgets === true ? [{
                                    field: 'data.local.widgets',
                                    label: '部件',
                                    format: 'number'
                                  }] : []),
                                  ...(formData.widget?.showBookmarks === true ? [{
                                    field: 'data.local.bookmarks',
                                    label: '书签',
                                    format: 'number'
                                  }] : []),
                                  ...(formData.widget?.showGroups === true ? [{
                                    field: 'data.local.totalGroups',
                                    label: '分组',
                                    format: 'number'
                                  }] : []),
                                  ...(formData.widget?.showBackups === true ? [{
                                    field: 'data.local.backups',
                                    label: '备份',
                                    format: 'number'
                                  }] : [])
                                ]
                              }
                            };
                            return JSON.stringify(config, null, 2);
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Homepage统计专用配置 */}
                {watchedWidgetType === 'homepage-stats' && (
                  <div className="space-y-4">
                    {/* Homepage统计说明提示 */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">📊</span>
                        </div>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">Homepage Config统计小部件说明</p>
                          <p className="text-xs text-blue-300 mb-2">
                            显示Homepage Config Manager项目的统计信息，包括服务总数、小部件数量、书签数量、分组数量、备份数量等。
                          </p>
                          <div className="text-xs text-blue-300 space-y-1">
                            <p>• <strong>本地统计:</strong> 显示当前配置的统计信息</p>
                            <p>• <strong>Homepage统计:</strong> 可选，连接Homepage实例获取实际运行统计</p>
                            <p>• <strong>对比功能:</strong> 验证配置同步情况</p>
                            <p>• <strong>密码保护:</strong> 支持Homepage密码保护</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 统计配置 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Homepage地址 (可选)
                        </label>
                        <input
                          {...register('widget.homepageUrl')}
                          className="input"
                          placeholder="http://localhost:3000"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          可选，用于获取Homepage实例的统计信息
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          访问密码 (可选)
                        </label>
                        <input
                          {...register('widget.homepagePassword')}
                          type="text"
                          className="input"
                          placeholder="Homepage访问密码"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          可选，如果Homepage设置了密码保护
                        </p>
                      </div>
                    </div>

                    {/* 显示选项 */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        显示选项
                      </label>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showServices')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">服务总数</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showWidgets')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">小部件数量</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showBookmarks')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">书签数量</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showGroups')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">分组总数</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.showBackups')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">备份数量</span>
                        </label>

                      </div>
                    </div>

                    {/* 刷新配置 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          刷新间隔 (秒)
                        </label>
                        <input
                          {...register('widget.refreshInterval', { valueAsNumber: true })}
                          type="number"
                          min="30"
                          className="input"
                          placeholder="60"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                          建议30-3600秒，避免频繁请求
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示模式
                        </label>
                        <select
                          {...register('widget.display')}
                          className="select"
                        >
                          <option value="block">块状显示</option>
                          <option value="list">列表显示</option>
                          <option value="grid">网格显示</option>
                        </select>
                      </div>
                    </div>

                    {/* 配置预览 */}
                    <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                      <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                      <pre className="text-xs text-gray-400 overflow-x-auto">
                        {(() => {
                          const homepageUrl = watch('widget.homepageUrl') || ''
                          const homepagePassword = watch('widget.homepagePassword') || ''
                          const refreshInterval = watch('widget.refreshInterval') || 60
                          const display = watch('widget.display') || 'block'
                          const showServices = watch('widget.showServices')
                          const showWidgets = watch('widget.showWidgets')
                          const showBookmarks = watch('widget.showBookmarks')
                          const showGroups = watch('widget.showGroups')
                          const showBackups = watch('widget.showBackups')

                          const mappings = []
                          if (showWidgets) mappings.push({ field: 'data.local.widgets', label: '部件', format: 'number' })
                          if (showServices) mappings.push({ field: 'data.local.services', label: '服务', format: 'number' })
                          if (showBookmarks) mappings.push({ field: 'data.local.bookmarks', label: '书签', format: 'number' })
                          if (showGroups) mappings.push({ field: 'data.local.totalGroups', label: '分组', format: 'number' })
                          if (showBackups) mappings.push({ field: 'data.local.backups', label: '备份', format: 'number' })

                          let config = `widget:
  type: customapi
  url: ${window.location.protocol}//${window.location.host}/api/homepage-stats
  refreshInterval: ${refreshInterval * 1000}
  method: GET
  display: ${display}`

                          if (homepageUrl) {
                            config += `\n  headers:`
                            config += `\n    X-Homepage-URL: ${homepageUrl}`
                            if (homepagePassword) {
                              config += `\n    X-Homepage-Password: ${homepagePassword}`
                            }
                          }

                          if (mappings.length > 0) {
                            config += `\n  mappings:`
                            mappings.forEach(mapping => {
                              config += `\n    - field: ${mapping.field}`
                              config += `\n      label: ${mapping.label}`
                              config += `\n      format: ${mapping.format}`
                            })
                          }

                          return config
                        })()}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Tautulli (Plex) 专用配置 */}
                {watchedWidgetType === 'tautulli' && (
                  <div className="space-y-4">
                    {/* Tautulli 说明提示 */}
                    <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">T</span>
                        </div>
                        <div className="text-sm text-yellow-200">
                          <p className="font-medium mb-1">Tautulli (Plex) 监控小部件说明</p>
                          <p className="text-yellow-300 mb-2">
                            提供 Plex 服务器的详细活动流信息，比基础 Plex 小部件更详细。
                          </p>
                          <div className="text-xs text-yellow-400 space-y-1">
                            <p>• <strong>URL:</strong> Tautulli 服务器地址 (如: http://tautulli.host:8181)</p>
                            <p>• <strong>API Key:</strong> 在 Tautulli 设置 → Web 界面 → API 中获取</p>
                            <p>• <strong>功能:</strong> 显示当前活动流的详细信息</p>
                            <p>• <strong>无可配置字段:</strong> 该小部件无额外字段配置</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                        Tautulli 连接配置
                      </h4>

                      {/* 连接配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Tautulli URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Tautulli 服务器地址是必填的' })}
                            className="input"
                            placeholder="http://tautulli.host:8181"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Tautulli API Key *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Tautulli API Key 是必填的' })}
                            className="input"
                            placeholder="your_tautulli_api_key"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                        Tautulli 显示配置
                      </h4>

                      {/* Tautulli 特殊选项 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示选项
                        </label>
                        <div className="space-y-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.enableUser')}
                              className="border-gray-600 text-yellow-600 focus:ring-yellow-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示用户信息</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.showEpisodeNumber')}
                              className="border-gray-600 text-yellow-600 focus:ring-yellow-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">显示剧集编号</span>
                            <span className="text-xs text-gray-500">- 默认: false</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.expandOneStreamToTwoRows')}
                              className="border-gray-600 text-yellow-600 focus:ring-yellow-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">单流扩展为两行显示</span>
                            <span className="text-xs text-gray-500">- 默认: true</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://tautulli.host.or.ip'
                            const key = watch('widget.key') || 'your_tautulli_api_key'
                            const enableUser = watch('widget.enableUser')
                            const showEpisodeNumber = watch('widget.showEpisodeNumber')
                            const expandOneStreamToTwoRows = watch('widget.expandOneStreamToTwoRows')

                            let config = `widget:
  type: tautulli
  url: ${url}
  key: ${key}`

                            if (enableUser) {
                              config += `
  enableUser: true`
                            }
                            if (showEpisodeNumber) {
                              config += `
  showEpisodeNumber: true`
                            }
                            if (!expandOneStreamToTwoRows) {
                              config += `
  expandOneStreamToTwoRows: false`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Gitea 专用配置 */}
                {watchedWidgetType === 'gitea' && (
                  <div className="space-y-4">
                    {/* Gitea 说明提示 */}
                    <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">G</span>
                        </div>
                        <div className="text-sm text-emerald-200">
                          <p className="font-medium mb-1">Gitea 代码仓库小部件说明</p>
                          <p className="text-emerald-300 mb-2">
                            显示 Gitea 的仓库、通知、问题和 PR 统计信息。
                          </p>
                          <div className="text-xs text-emerald-400 space-y-1">
                            <p>• <strong>URL:</strong> Gitea 服务器地址 (如: http://gitea.host.or.ip:port)</p>
                            <p>• <strong>API Token:</strong> 需要 notifications、repository 和 issue 权限</p>
                            <p>• <strong>支持字段:</strong> ["repositories", "notifications", "issues", "pulls"]</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                        Gitea 基础配置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Gitea 服务器 URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Gitea URL 是必填的' })}
                            className="input"
                            placeholder="http://gitea.host.or.ip:port"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Gitea API Token *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Gitea API Token 是必填的' })}
                            className="input"
                            type="text"
                            placeholder="your_gitea_api_token"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                        Gitea 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.repositories')}
                              className="border-gray-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">仓库</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.notifications')}
                              className="border-gray-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">通知</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.issues')}
                              className="border-gray-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">问题</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.pulls')}
                              className="border-gray-600 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">拉取请求</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://gitea.host.or.ip:port'
                            const key = watch('widget.key') || 'giteaapitoken'
                            const fields = []
                            if (watch('widget.fields.repositories') !== false) fields.push('repositories')
                            if (watch('widget.fields.notifications')) fields.push('notifications')
                            if (watch('widget.fields.issues')) fields.push('issues')
                            if (watch('widget.fields.pulls')) fields.push('pulls')

                            let config = `widget:
  type: gitea
  url: ${url}
  key: ${key}`

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Gitlab 专用配置 */}
                {watchedWidgetType === 'gitlab' && (
                  <div className="space-y-4">
                    {/* Gitlab 说明提示 */}
                    <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">GL</span>
                        </div>
                        <div className="text-sm text-orange-200">
                          <p className="font-medium mb-1">Gitlab 代码仓库小部件说明</p>
                          <p className="text-orange-300 mb-2">
                            显示 Gitlab 的项目、事件、问题和合并请求统计信息。
                          </p>
                          <div className="text-xs text-orange-400 space-y-1">
                            <p>• <strong>URL:</strong> Gitlab 服务器地址 (如: http://gitlab.host.or.ip:port)</p>
                            <p>• <strong>API Token:</strong> 需要 read_api 或 api 权限的个人访问令牌</p>
                            <p>• <strong>User ID:</strong> 您的 Gitlab 用户 ID（可在个人资料页面找到）</p>
                            <p>• <strong>支持字段:</strong> ["events", "issues", "merges", "projects"]</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                        Gitlab 基础配置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Gitlab 服务器 URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Gitlab URL 是必填的' })}
                            className="input"
                            placeholder="http://gitlab.host.or.ip:port"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Gitlab 个人访问令牌 *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Gitlab 个人访问令牌是必填的' })}
                            className="input"
                            type="text"
                            placeholder="personal-access-token"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Gitlab 用户 ID *
                        </label>
                        <input
                          {...register('widget.user_id', { required: 'Gitlab 用户 ID 是必填的' })}
                          className="input"
                          type="number"
                          placeholder="123456"
                        />
                        {errors.widget?.user_id && (
                          <p className="text-red-500 text-sm mt-1">{errors.widget.user_id.message}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">您的用户 ID 可以在 Gitlab 个人资料页面找到</p>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                        Gitlab 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.events')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">事件</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.issues')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">问题</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.merges')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">合并请求</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.projects')}
                              className="border-gray-600 text-orange-600 focus:ring-orange-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">项目</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://gitlab.host.or.ip:port'
                            const key = watch('widget.key') || 'personal-access-token'
                            const user_id = watch('widget.user_id') || '123456'
                            const fields = []
                            if (watch('widget.fields.events') !== false) fields.push('events')
                            if (watch('widget.fields.issues')) fields.push('issues')
                            if (watch('widget.fields.merges')) fields.push('merges')
                            if (watch('widget.fields.projects')) fields.push('projects')

                            let config = `widget:
  type: gitlab
  url: ${url}
  key: ${key}
  user_id: ${user_id}`

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Prowlarr 专用配置 */}
                {watchedWidgetType === 'prowlarr' && (
                  <div className="space-y-4">
                    {/* Prowlarr 说明提示 */}
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">P</span>
                        </div>
                        <div className="text-sm text-purple-200">
                          <p className="font-medium mb-1">Prowlarr 索引器管理小部件说明</p>
                          <p className="text-purple-300 mb-2">
                            显示 Prowlarr 的抓取统计和查询信息。
                          </p>
                          <div className="text-xs text-purple-400 space-y-1">
                            <p>• <strong>URL:</strong> Prowlarr 服务器地址 (如: http://prowlarr.host.or.ip)</p>
                            <p>• <strong>API Key:</strong> 在 Settings → General 中获取</p>
                            <p>• <strong>支持字段:</strong> ["numberOfGrabs", "numberOfQueries", "numberOfFailGrabs", "numberOfFailQueries"]</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                        Prowlarr 基础配置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Prowlarr 服务器 URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Prowlarr URL 是必填的' })}
                            className="input"
                            placeholder="http://prowlarr.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Prowlarr API Key *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Prowlarr API Key 是必填的' })}
                            className="input"
                            type="text"
                            placeholder="apikeyapikeyapikeyapikeyapikey"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                        Prowlarr 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.numberOfGrabs')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">成功抓取</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.numberOfQueries')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">查询次数</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.numberOfFailGrabs')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">失败抓取</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.numberOfFailQueries')}
                              className="border-gray-600 text-purple-600 focus:ring-purple-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">失败查询</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://prowlarr.host.or.ip'
                            const key = watch('widget.key') || 'apikeyapikeyapikeyapikeyapikey'
                            const numberOfGrabs = watch('widget.fields.numberOfGrabs')
                            const numberOfQueries = watch('widget.fields.numberOfQueries')
                            const numberOfFailGrabs = watch('widget.fields.numberOfFailGrabs')
                            const numberOfFailQueries = watch('widget.fields.numberOfFailQueries')

                            const fields = []
                            if (numberOfGrabs) fields.push('numberOfGrabs')
                            if (numberOfQueries) fields.push('numberOfQueries')
                            if (numberOfFailGrabs) fields.push('numberOfFailGrabs')
                            if (numberOfFailQueries) fields.push('numberOfFailQueries')

                            let config = `widget:
  type: prowlarr
  url: ${url}
  key: ${key}`

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Radarr 专用配置 */}
                {watchedWidgetType === 'radarr' && (
                  <div className="space-y-4">
                    {/* Radarr 说明提示 */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">R</span>
                        </div>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">Radarr 电影管理小部件说明</p>
                          <p className="text-blue-300 mb-2">
                            显示 Radarr 的电影统计和队列信息。
                          </p>
                          <div className="text-xs text-blue-400 space-y-1">
                            <p>• <strong>URL:</strong> Radarr 服务器地址 (如: http://radarr.host.or.ip)</p>
                            <p>• <strong>API Key:</strong> 在 Settings → General 中获取</p>
                            <p>• <strong>支持字段:</strong> ["wanted", "missing", "queued", "movies"]</p>
                            <p>• <strong>enableQueue:</strong> 可选，启用详细队列列表 (默认: false)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        Radarr 基础配置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Radarr 服务器 URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Radarr URL 是必填的' })}
                            className="input"
                            placeholder="http://radarr.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Radarr API Key *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Radarr API Key 是必填的' })}
                            className="input"
                            type="text"
                            placeholder="apikeyapikeyapikeyapikeyapikey"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        Radarr 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.wanted')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">想要</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.missing')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">缺失</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.queued')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">队列</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.movies')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">电影</span>
                          </label>
                        </div>
                      </div>

                      {/* 高级选项 */}
                      <div className="mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.enableQueue')}
                            className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">启用详细队列列表 (enableQueue)</span>
                        </label>
                        <p className="text-xs text-gray-400 mt-1">启用后显示详细的队列信息，默认关闭</p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://radarr.host.or.ip'
                            const key = watch('widget.key') || 'apikeyapikeyapikeyapikeyapikey'
                            const wanted = watch('widget.fields.wanted')
                            const missing = watch('widget.fields.missing')
                            const queued = watch('widget.fields.queued')
                            const movies = watch('widget.fields.movies')
                            const enableQueue = watch('widget.enableQueue')

                            const fields = []
                            if (wanted) fields.push('wanted')
                            if (missing) fields.push('missing')
                            if (queued) fields.push('queued')
                            if (movies) fields.push('movies')

                            let config = `widget:
  type: radarr
  url: ${url}
  key: ${key}`

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            if (enableQueue) {
                              config += `
  enableQueue: true`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sonarr 专用配置 */}
                {watchedWidgetType === 'sonarr' && (
                  <div className="space-y-4">
                    {/* Sonarr 说明提示 */}
                    <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">S</span>
                        </div>
                        <div className="text-sm text-green-200">
                          <p className="font-medium mb-1">Sonarr 电视剧管理小部件说明</p>
                          <p className="text-green-300 mb-2">
                            显示 Sonarr 的电视剧统计和队列信息。
                          </p>
                          <div className="text-xs text-green-400 space-y-1">
                            <p>• <strong>URL:</strong> Sonarr 服务器地址 (如: http://sonarr.host.or.ip)</p>
                            <p>• <strong>API Key:</strong> 在 Settings → General 中获取</p>
                            <p>• <strong>支持字段:</strong> ["wanted", "queued", "series"]</p>
                            <p>• <strong>enableQueue:</strong> 可选，启用详细队列列表 (默认: false)</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Sonarr 基础配置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Sonarr 服务器 URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Sonarr URL 是必填的' })}
                            className="input"
                            placeholder="http://sonarr.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Sonarr API Key *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Sonarr API Key 是必填的' })}
                            className="input"
                            type="text"
                            placeholder="apikeyapikeyapikeyapikeyapikey"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                        Sonarr 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.wanted')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">想要</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.queued')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">队列</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.series')}
                              className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">剧集</span>
                          </label>
                        </div>
                      </div>

                      {/* 高级选项 */}
                      <div className="mb-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            {...register('widget.enableQueue')}
                            className="border-gray-600 text-green-600 focus:ring-green-500 focus:ring-offset-gray-800"
                          />
                          <span className="text-sm text-gray-300">启用详细队列列表 (enableQueue)</span>
                        </label>
                        <p className="text-xs text-gray-400 mt-1">启用后显示详细的队列信息，默认关闭</p>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://sonarr.host.or.ip'
                            const key = watch('widget.key') || 'apikeyapikeyapikeyapikeyapikey'
                            const wanted = watch('widget.fields.wanted')
                            const queued = watch('widget.fields.queued')
                            const series = watch('widget.fields.series')
                            const enableQueue = watch('widget.enableQueue')

                            const fields = []
                            if (wanted) fields.push('wanted')
                            if (queued) fields.push('queued')
                            if (series) fields.push('series')

                            let config = `widget:
  type: sonarr
  url: ${url}
  key: ${key}`

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            if (enableQueue) {
                              config += `
  enableQueue: true`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Jellyseerr 专用配置 */}
                {watchedWidgetType === 'jellyseerr' && (
                  <div className="space-y-4">
                    {/* Jellyseerr 说明提示 */}
                    <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">J</span>
                        </div>
                        <div className="text-sm text-indigo-200">
                          <p className="font-medium mb-1">Jellyseerr 请求管理小部件说明</p>
                          <p className="text-indigo-300 mb-2">
                            显示 Jellyseerr 的请求统计信息。
                          </p>
                          <div className="text-xs text-indigo-400 space-y-1">
                            <p>• <strong>URL:</strong> Jellyseerr 服务器地址 (如: http://jellyseerr.host.or.ip)</p>
                            <p>• <strong>API Key:</strong> 在 Settings → General → API Key 中获取</p>
                            <p>• <strong>支持字段:</strong> ["pending", "approved", "available"]</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                        Jellyseerr 基础配置
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Jellyseerr 服务器 URL *
                          </label>
                          <input
                            {...register('widget.url', { required: 'Jellyseerr URL 是必填的' })}
                            className="input"
                            placeholder="http://jellyseerr.host.or.ip"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.url.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Jellyseerr API Key *
                          </label>
                          <input
                            {...register('widget.key', { required: 'Jellyseerr API Key 是必填的' })}
                            className="input"
                            type="text"
                            placeholder="apikeyapikeyapikeyapikeyapikey"
                          />
                          {errors.widget?.key && (
                            <p className="text-red-500 text-sm mt-1">{errors.widget.key.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                        Jellyseerr 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.pending')}
                              className="border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">待处理</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.approved')}
                              className="border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已批准</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.available')}
                              className="border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已可用</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://jellyseerr.host.or.ip'
                            const key = watch('widget.key') || 'apikeyapikeyapikeyapikeyapikey'
                            const pending = watch('widget.fields.pending')
                            const approved = watch('widget.fields.approved')
                            const available = watch('widget.fields.available')

                            const fields = []
                            if (pending) fields.push('pending')
                            if (approved) fields.push('approved')
                            if (available) fields.push('available')

                            let config = `widget:
  type: jellyseerr
  url: ${url}
  key: ${key}`

                            if (fields.length > 0) {
                              config += `
  fields: [${fields.map(f => `"${f}"`).join(', ')}]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Navidrome 专用配置 */}
                {watchedWidgetType === 'navidrome' && (
                  <div className="space-y-4">
                    {/* Navidrome 说明提示 */}
                    <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">🎵</span>
                        </div>
                        <div className="text-sm text-purple-200">
                          <p className="font-medium mb-1">Navidrome 音乐服务器小部件说明</p>
                          <p className="text-purple-300 mb-2">
                            显示 Navidrome 音乐服务器的信息。Navidrome 是一个现代化的音乐服务器和流媒体器，兼容 Subsonic/Airsonic API。
                          </p>
                          <div className="text-xs text-purple-400 space-y-1">
                            <p>• <strong>URL:</strong> Navidrome 服务器地址 (如: http://navidrome.host.or.ip:port)</p>
                            <p>• <strong>用户名:</strong> Navidrome 登录用户名</p>
                            <p>• <strong>Token:</strong> MD5(password + salt) 格式的认证令牌</p>
                            <p>• <strong>Salt:</strong> 随机盐值，用于生成 token</p>
                            <p>• <strong>无可配置字段:</strong> 此小部件没有可配置的字段</p>
                            <p>• <strong>API 参考:</strong> <a href="http://www.subsonic.org/pages/api.jsp" target="_blank" className="text-purple-300 hover:text-purple-200 underline">Subsonic API 文档</a></p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                        Navidrome 连接配置
                      </h4>

                      {/* 连接配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Navidrome 服务器 URL <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.url', { required: 'Navidrome 服务器地址是必填的' })}
                            className="input"
                            placeholder="http://navidrome.host.or.ip:port"
                          />
                          {errors.widget?.url && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.url.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Navidrome 服务器的完整地址，包含端口号
                          </p>
                        </div>
                      </div>

                      {/* 认证配置 */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            用户名 <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.user', { required: 'Navidrome 用户名是必填的' })}
                            className="input"
                            placeholder="username"
                          />
                          {errors.widget?.user && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.user.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            Navidrome 登录用户名
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Salt (随机盐值) <span className="text-red-400">*</span>
                          </label>
                          <input
                            {...register('widget.salt', { required: 'Salt 是必填的' })}
                            className="input"
                            placeholder="randomsalt"
                          />
                          {errors.widget?.salt && (
                            <p className="text-red-400 text-xs mt-1">{errors.widget.salt.message}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-400">
                            用于生成 token 的随机盐值
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Token <span className="text-red-400">*</span>
                        </label>
                        <input
                          {...register('widget.token', { required: 'Token 是必填的' })}
                          className="input"
                          placeholder="token#md5(password + salt)"
                        />
                        {errors.widget?.token && (
                          <p className="text-red-400 text-xs mt-1">{errors.widget.token.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-400">
                          格式：token#md5(password + salt)，其中 token 是随机字符串，md5 部分是密码和盐值的 MD5 哈希
                        </p>
                      </div>

                      {/* 说明信息 */}
                      <div className="p-3 bg-purple-950/40 rounded border border-purple-600/20">
                        <h5 className="text-sm font-medium text-purple-300 mb-2">💡 Token 生成说明</h5>
                        <div className="text-xs text-purple-200 space-y-1">
                          <p>• Token 格式：<code className="bg-gray-800 px-1 rounded">token#md5hash</code></p>
                          <p>• 其中 token 是任意随机字符串（如：randomsalt）</p>
                          <p>• md5hash 是 MD5(password + salt) 的结果</p>
                          <p>• 例如：密码是 "mypass"，盐值是 "randomsalt"</p>
                          <p>• 则 MD5("mypass" + "randomsalt") = MD5("mypassrandomsalt")</p>
                          <p>• 最终 token：randomsalt#生成的md5值</p>
                          <p>• 详细信息请参考：<a href="http://www.subsonic.org/pages/api.jsp" target="_blank" className="text-purple-300 hover:text-purple-200 underline">Subsonic API 文档</a></p>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://navidrome.host.or.ip:port'
                            const user = watch('widget.user') || 'username'
                            const token = watch('widget.token') || 'token#md5(password + salt)'
                            const salt = watch('widget.salt') || 'randomsalt'

                            let config = `widget:
  type: navidrome
  url: ${url}
  user: ${user}
  token: ${token}
  salt: ${salt}`

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Overseerr 专用配置 */}
                {watchedWidgetType === 'overseerr' && (
                  <div className="space-y-4">
                    {/* Overseerr 说明提示 */}
                    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">O</span>
                        </div>
                        <div className="text-sm text-blue-200">
                          <p className="font-medium mb-1">Overseerr 媒体请求管理小部件说明</p>
                          <p className="text-blue-300 mb-2">
                            显示 Overseerr 的媒体请求统计信息。
                          </p>
                          <div className="text-xs text-blue-400 space-y-1">
                            <p>• <strong>URL:</strong> Overseerr 服务器地址 (如: http://overseerr.host:5055)</p>
                            <p>• <strong>API Key:</strong> 在 Overseerr 设置 → 常规 中获取</p>
                            <p>• <strong>支持字段:</strong> ["pending", "approved", "available", "processing"]</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                        Overseerr 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.pending')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">待处理</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.approved')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已批准</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.available')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已可用</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.processing')}
                              className="border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">处理中</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://overseerr.host.or.ip'
                            const key = watch('widget.key') || 'your_overseerr_api_key'
                            const pending = watch('widget.fields.pending')
                            const approved = watch('widget.fields.approved')
                            const available = watch('widget.fields.available')
                            const processing = watch('widget.fields.processing')

                            const fields = []
                            if (pending) fields.push('pending')
                            if (approved) fields.push('approved')
                            if (available) fields.push('available')
                            if (processing) fields.push('processing')

                            let config = `widget:
  type: overseerr
  url: ${url}
  key: ${key}`

                            if (fields.length > 0) {
                              config += `
  fields: ["${fields.join('", "')}"]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ombi 专用配置 */}
                {watchedWidgetType === 'ombi' && (
                  <div className="space-y-4">
                    {/* Ombi 说明提示 */}
                    <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-lg p-3">
                      <div className="flex items-start space-x-2">
                        <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-xs">O</span>
                        </div>
                        <div className="text-sm text-indigo-200">
                          <p className="font-medium mb-1">Ombi 媒体请求管理小部件说明</p>
                          <p className="text-indigo-300 mb-2">
                            显示 Ombi 的媒体请求统计信息。
                          </p>
                          <div className="text-xs text-indigo-400 space-y-1">
                            <p>• <strong>URL:</strong> Ombi 服务器地址 (如: http://ombi.host:3579)</p>
                            <p>• <strong>API Key:</strong> 在 Ombi 设置 → 配置 → 常规 中获取</p>
                            <p>• <strong>支持字段:</strong> ["pending", "approved", "available"]</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                      <h4 className="text-lg font-medium text-gray-200 mb-4 flex items-center">
                        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></span>
                        Ombi 显示配置
                      </h4>

                      {/* 显示字段选择 */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          显示字段 <span className="text-xs text-gray-400">(可多选)</span>
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.pending')}
                              className="border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">待处理</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.approved')}
                              className="border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已批准</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              {...register('widget.fields.available')}
                              className="border-gray-600 text-indigo-600 focus:ring-indigo-500 focus:ring-offset-gray-800"
                            />
                            <span className="text-sm text-gray-300">已可用</span>
                          </label>
                        </div>
                      </div>

                      {/* 配置预览 */}
                      <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-600">
                        <h5 className="text-sm font-medium text-gray-300 mb-2">配置预览</h5>
                        <pre className="text-xs text-gray-400 overflow-x-auto">
                          {(() => {
                            const url = watch('widget.url') || 'http://ombi.host.or.ip'
                            const key = watch('widget.key') || 'your_ombi_api_key'
                            const pending = watch('widget.fields.pending')
                            const approved = watch('widget.fields.approved')
                            const available = watch('widget.fields.available')

                            const fields = []
                            if (pending) fields.push('pending')
                            if (approved) fields.push('approved')
                            if (available) fields.push('available')

                            let config = `widget:
  type: ombi
  url: ${url}
  key: ${key}`

                            if (fields.length > 0) {
                              config += `
  fields: ["${fields.join('", "')}"]`
                            }

                            return config
                          })()}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

              </div>

              {/* 操作按钮 */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {service ? '更新服务' : '添加服务'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* 图标选择器模态框 */}
      {showIconSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">选择图标</h3>
              <button
                onClick={() => setShowIconSelector(false)}
                className="text-gray-400 hover:text-white"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {availableIcons.map((icon, index) => (
                <div
                  key={index}
                  onClick={() => handleSelectIcon(icon.url)}
                  className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg cursor-pointer hover:bg-gray-600 transition-colors"
                >
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    {icon.type === 'favicon' ? (
                      <img
                        src={icon.url}
                        alt={icon.name}
                        className="w-6 h-6 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none'
                          e.target.nextSibling.style.display = 'block'
                        }}
                      />
                    ) : (
                      getIconPreviewUrl(icon.url) ? (
                        <img
                          src={getIconPreviewUrl(icon.url)}
                          alt={icon.name}
                          className="w-6 h-6 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'block'
                          }}
                        />
                      ) : null
                    )}
                    <span
                      className="text-xs text-gray-400 hidden"
                    >
                      ?
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate">{icon.name}</p>
                    <p className="text-xs text-gray-400 truncate">{icon.url}</p>
                  </div>
                </div>
              ))}
            </div>

            {availableIcons.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-400">未找到可用的图标</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 图标搜索模态框 */}
      <IconSearchModal
        isOpen={showIconSearch}
        onClose={() => setShowIconSearch(false)}
        onSelectIcon={handleSelectSearchIcon}
      />

      {/* 本地图标库模态框 */}
      <LocalIconsModal
        isOpen={showLocalIcons}
        onClose={() => setShowLocalIcons(false)}
        onSelectIcon={handleSelectLocalIcon}
      />
    </>
  )
}

export default ServiceEditModal
